// 🔑 THE UNDERWRITING MODULE FOR A GUIDED FILE, TO THE APPROVED MOCKUPS (Jesse, 2026-10-26 — the approved
// redesign mockups). Brand tokens only: Inter, black text, navy labels, white cards on
// the neutral ground, blue buttons with white text, the yellow top edge on a step's headline card, statusColors for
// state (green done, tan pending / medium, red high).
//   FraudBanner      — the file-wide banner on every step: the count, the high count, a link to each flag's step
//   FlagList         — a step's own open flags (the step that owns the flag's document — backend utils/fraudFlagPlacement):
//                      the document (name, type, upload date), what was found in plain words, what to check, and
//                      Dismiss with a reason / Keep and request a document / Escalate (no Keep on an ID-check flag, 2026-10-28)
//   GuidedIncomeStep — qualifying monthly income, per source with the rule named, the arithmetic and the documents;
//                      "Accept income" / "Change the figure" (per source, a reason, the borrower re-confirms at step 6)
//   GuidedAssetsStep — qualifying liquid assets, one line per account (type · bank ••last4, statement date, verified
//                      balance, what the borrower planned — information only); Approve / "Change a figure"
// Guided files never show the old Override (it is refused for them): changes go through the guided path.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { identityStaffState } from '../../../utils/identityAdminLabel';
import SentIcon from '../../SentIcon';
import { CalculatedLines } from '../../doorlypro/submission/LoSubmissionFlow';

export const T = Object.freeze({
  ink: '#000000', navy: '#1A3A6B', blue: '#3a6ad9', ground: '#f7f7f7', line: '#e6e6e6', white: '#ffffff',
  yellow: '#fffd5b', green: '#acf44e', tan: '#FFDD86', red: '#ff082f', slateLight: '#E7EAF0',
  font: "Inter, -apple-system, 'Segoe UI', Roboto, sans-serif",
});
const money = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : `$${Math.round(Number(n)).toLocaleString('en-US')}`);
const day = (t) => { const d = new Date(t); return Number.isNaN(+d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
const dayTime = (t) => { const d = new Date(t); return Number.isNaN(+d) ? '' : `${day(t)}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`; };
const SEV = { high: { bg: T.red, fg: T.white, label: 'HIGH' }, medium: { bg: T.tan, fg: T.ink, label: 'MEDIUM' }, low: { bg: T.slateLight, fg: T.ink, label: 'LOW' } };

export const card = (extra = {}) => ({ background: T.white, border: `1px solid ${T.line}`, borderRadius: 12, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, ...extra });
export const label = { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.navy };
export const btn = (kind = 'secondary', disabled = false) => ({
  minHeight: 44, padding: '0 16px', borderRadius: 10, font: `700 14px ${T.font}`, cursor: disabled ? 'not-allowed' : 'pointer',
  border: kind === 'primary' ? 'none' : `1px solid ${T.ink}`,
  background: disabled ? T.line : kind === 'primary' ? T.blue : T.white,
  color: disabled ? T.ink : kind === 'primary' ? T.white : T.ink,
});
const input = { minHeight: 40, padding: '0 10px', borderRadius: 8, border: `1px solid ${T.ink}`, font: `500 14px ${T.font}`, color: T.ink, boxSizing: 'border-box' };

/** The file's open flags, placed on their steps (GET /api/admin/borrowers/:id/fraud-flags/placed). */
export function usePlacedFlags(API, borrowerId, headers, refreshKey = 0) {
  const [flags, setFlags] = useState(null);
  const [summary, setSummary] = useState(null);
  const load = useCallback(async () => {
    if (!borrowerId) return;
    try { const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/fraud-flags/placed`, { headers }); setFlags(r.data.flags || []); setSummary(r.data.summary || null); }
    catch (_) { setFlags([]); setSummary(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, borrowerId, refreshKey]);
  useEffect(() => { load(); }, [load]);
  const counts = useMemo(() => {
    // 🔑 THE ONE FILE-WIDE COUNT (hotfix 11-01, backend fraudFlagPlacement.fileFlagSummary): the step strip reads the
    // same per-step numbers the Review tab banner is built from. The local tally is only a fallback.
    if (summary && summary.byModule) return { ...summary.byModule };
    const c = {};
    // A test account's flags count like any other (labelled TEST ACCOUNT — backend fraudFlagPlacement, 2026-10-31).
    for (const f of flags || []) c[f.module] = (c[f.module] || 0) + 1;
    return c;
  }, [flags, summary]);
  return { flags: flags || [], counts, summary, reload: load, loaded: flags !== null };
}

/** The file-wide banner at the top of every step. */
export function FraudBanner({ flags: allFlags, onGoTo }) {
  // Every open flag, a test account's included (2026-10-31).
  const flags = allFlags || [];
  if (!flags.length) return null;
  const high = flags.filter((f) => f.severity === 'high').length;
  return (
    <div data-testid="uw-fraud-banner" role="status" style={{ ...card({ borderTop: `5px solid ${T.red}`, padding: '14px 18px', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }) }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>{flags.length} open fraud flag{flags.length === 1 ? '' : 's'} on this file{high ? ` · ${high} high` : ''}</div>
        <div style={{ fontSize: 13 }}>{high ? 'A high flag holds the borrower’s credit pull until it is resolved. ' : ''}Every flag must be closed before an offer.</div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {flags.map((f) => (
          <button key={f.id} type="button" data-testid="uw-fraud-banner-link" data-module={f.module} onClick={() => onGoTo && onGoTo(f.module, f.id)}
            style={{ minHeight: 36, padding: '0 12px', borderRadius: 8, background: T.ground, border: 'none', font: `600 13px ${T.font}`, color: T.ink, cursor: 'pointer' }}>
            {f.moduleLabel} · {f.document && f.document.typeLabel ? f.document.typeLabel : f.title} · {f.severity}
          </button>
        ))}
      </div>
    </div>
  );
}

// 🔑 A HELD OR FLAGGED ID'S EVIDENCE AND DECISION (Jesse, 2026-10-28 — held IDs are decided in the fraud queue): the
// labelled ID images (opened with the staff token) and what a dismissal does. No "Keep and request" on an ID flag: the
// borrower only ever sees the automatic retry.
export function IdCheckEvidence({ API, flag, headers }) {
  const [err, setErr] = useState(null);
  if (!flag || !flag.idCheck) return null;
  const view = async (img) => {
    setErr(null);
    try {
      const r = await axios.get(`${API}/api/admin/borrowers/${flag.userId}/documents/${img.documentId}/view`, { headers, responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank', 'noopener');
    } catch (_) { setErr('That image could not be opened just now.'); }
  };
  return (
    <div data-testid="uw-id-check-evidence" style={{ display: 'grid', gap: 8, fontSize: 13, background: T.ground, borderRadius: 8, padding: '10px 12px' }}>
      {(flag.images || []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <b>ID images:</b>
          {flag.images.map((img) => (
            <button key={img.documentId} type="button" data-testid="uw-id-check-image" onClick={() => view(img)}
              style={{ minHeight: 36, padding: '0 12px', borderRadius: 8, background: T.white, border: `1px solid ${T.ink}`, font: `600 13px ${T.font}`, color: T.ink, cursor: 'pointer' }}>View {img.label}</button>
          ))}
        </div>
      )}
      <div>Dismissing the last open ID flag clears the ID — the borrower is told only that they can continue. Escalate if it needs underwriting.</div>
      {err && <div role="alert" style={{ fontWeight: 700 }}>{err}</div>}
    </div>
  );
}

const DISMISS_REASONS = [['legitimate_explanation', 'There is a legitimate explanation'], ['borrower_provided_explanation', 'The borrower explained it'],
  ['verified_accurate', 'Verified as accurate'], ['duplicate_flag', 'Duplicate flag'], ['other', 'Other']];

/** One step's open flags, each with its document, plain words and the actions. */
export function FlagList({ API, borrowerId, headers, flags, module, onChanged }) {
  const mine = (flags || []).filter((f) => f.module === module);
  const [open, setOpen] = useState({});
  const [err, setErr] = useState(null);
  if (!mine.length) return null;
  const act = async (f, path, body) => {
    setErr(null);
    try {
      await axios.post(`${API}/api/admin/borrowers/${f.userId || borrowerId}/fraud-flags/${f.id}/${path}`, body || {}, { headers });
      setOpen((o) => ({ ...o, [f.id]: null }));
      if (onChanged) await onChanged();
    } catch (e) { setErr(e?.response?.data?.error || 'That could not be saved just now.'); }
  };
  return (
    <div data-testid={`uw-flags-${module}`} style={{ display: 'grid', gap: 14 }}>
      {err && <div role="alert" style={{ fontSize: 13, fontWeight: 700 }}>{err}</div>}
      {mine.map((f) => {
        const s = SEV[f.severity] || SEV.low;
        const o = open[f.id] || null;
        return (
          <div key={f.id} id={`uw-flag-${f.id}`} data-testid="uw-flag" data-severity={f.severity} data-test-account={f.testAccount ? 'true' : 'false'}
            data-reraises={f.reraisesFlagId || ''} style={card()}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', minHeight: 26, padding: '0 10px', borderRadius: 13, background: s.bg, color: s.fg,
                fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', border: f.severity === 'high' ? 'none' : `1px solid ${T.ink}` }}>{s.label}</span>
              {/* 🔑 A test account's flag counts and holds like any other; the label only says whose it is (2026-10-31). */}
              {f.testAccount && <span data-testid="uw-flag-test-account" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 26, padding: '0 10px', borderRadius: 13,
                background: T.slateLight, color: T.ink, fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', border: `1px solid ${T.ink}` }}>TEST ACCOUNT</span>}
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{f.title}</h3>
            </div>
            {f.reraisedLine && <div data-testid="uw-flag-reraised" style={{ fontSize: 13, fontWeight: 700 }}>{f.reraisedLine}</div>}
            <div data-testid="uw-flag-found" style={{ fontSize: 14, lineHeight: 1.6 }}>{f.found}</div>
            {f.document && (f.document.name || f.document.typeLabel) && (
              <div data-testid="uw-flag-document" style={{ fontSize: 13, background: T.ground, borderRadius: 8, padding: '10px 12px' }}>
                <b>Document:</b> {[f.document.name, f.document.typeLabel, f.document.uploadedAt ? `uploaded ${dayTime(f.document.uploadedAt)}` : null].filter(Boolean).join(' · ')}
              </div>
            )}
            <IdCheckEvidence API={API} flag={{ ...f, userId: f.userId || borrowerId }} headers={headers} />
            <div style={{ fontSize: 14, lineHeight: 1.6 }}><b>What to check:</b> {f.check}</div>
            <div style={{ fontSize: 13 }}><b>Holds:</b> {f.holds}</div>
            {!o && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button type="button" data-testid="uw-flag-dismiss" onClick={() => setOpen((x) => ({ ...x, [f.id]: { kind: 'dismiss', reason: '', notes: '' } }))} style={btn()}>Dismiss with a reason</button>
                {!f.idCheck && <button type="button" data-testid="uw-flag-keep" onClick={() => act(f, 'keep-request', {})} style={btn('primary')}>Keep and request a document</button>}
                <button type="button" data-testid="uw-flag-escalate" onClick={() => setOpen((x) => ({ ...x, [f.id]: { kind: 'escalate', notes: '' } }))} style={btn()}>Escalate</button>
              </div>
            )}
            {o && o.kind === 'dismiss' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Reason{' '}
                  <select data-testid="uw-flag-dismiss-reason" value={o.reason} onChange={(e) => setOpen((x) => ({ ...x, [f.id]: { ...o, reason: e.target.value } }))} style={input}>
                    <option value="">Choose…</option>
                    {DISMISS_REASONS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </select>
                </label>
                <input aria-label="Notes" placeholder="Notes (recorded on the file)" value={o.notes} onChange={(e) => setOpen((x) => ({ ...x, [f.id]: { ...o, notes: e.target.value } }))}
                  style={{ ...input, flex: '1 1 240px' }} />
                <button type="button" data-testid="uw-flag-dismiss-confirm" disabled={!o.reason} onClick={() => act(f, 'dismiss', { reason: o.reason, notes: o.notes })} style={btn('primary', !o.reason)}>Dismiss</button>
                <button type="button" onClick={() => setOpen((x) => ({ ...x, [f.id]: null }))} style={btn()}>Cancel</button>
              </div>
            )}
            {o && o.kind === 'escalate' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <input aria-label="What to look at" placeholder="What should they look at?" value={o.notes} onChange={(e) => setOpen((x) => ({ ...x, [f.id]: { ...o, notes: e.target.value } }))}
                  style={{ ...input, flex: '1 1 280px' }} />
                <button type="button" data-testid="uw-flag-escalate-confirm" onClick={() => act(f, 'escalate', { note: o.notes })} style={btn('primary')}>Escalate</button>
                <button type="button" onClick={() => setOpen((x) => ({ ...x, [f.id]: null }))} style={btn()}>Cancel</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function useFigures(API, borrowerId, headers, refreshKey) {
  const [figs, setFigs] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/module-figures`, { headers }); setFigs(r.data || {}); }
    catch (_) { setFigs({ income: null, assets: null }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, borrowerId, refreshKey]);
  useEffect(() => { load(); }, [load]);
  return { figs, reload: load };
}

function StatusCard({ accepted, acceptedAt, status, blockedBy }) {
  return (
    <div data-testid="uw-step-status" style={{ ...card({ flex: '1 1 300px' }) }}>
      <div style={label}>Status</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700 }}>
        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: accepted ? T.green : T.tan, border: `1px solid ${T.ink}` }} />
        {accepted ? `Accepted${acceptedAt ? ` on ${day(acceptedAt)}` : ''}` : 'Not accepted yet'}
      </div>
      <div data-testid="uw-step-status-line" style={{ fontSize: 14 }}>{status}</div>
      {blockedBy ? <div style={{ fontSize: 13 }}>{blockedBy}</div> : null}
    </div>
  );
}

function Headline({ testid, title, figure, sub, children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
      <div data-testid={testid} style={{ ...card({ flex: '2 1 480px', borderTop: `5px solid ${T.yellow}`, gap: 6 }) }}>
        <div style={label}>{title}</div>
        <div data-testid={`${testid}-figure`} style={{ fontSize: 44, fontWeight: 800, lineHeight: 1.1 }}>{figure}</div>
        <div style={{ fontSize: 14 }}>{sub}</div>
      </div>
      {children}
    </div>
  );
}

/** The guided Income step. */
/**
 * 🔑 THE INCOME CHECK, AS AN OPEN ITEM (Jesse 2026-11-01, fix-forward). When the income check cannot use the figure the
 * decision would rest on, it holds the decision (utils/incomeCheckFlags → hold income_check_open) and shows here, with the
 * figure on file, the cross-check's figure and the variance against the tolerance. A cross-check can be resolved by
 * staff ("the documents' figure stands", with a reason); the decision then runs by itself. Anything else clears itself
 * once its cause is fixed. Renders nothing when no check is open.
 */
function IncomeCheckPanel({ API, borrowerId, headers, refreshKey = 0, onChanged }) {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/income-check`, { headers }); setData(r.data || null); }
    catch (_) { setData(null); }
  }, [API, borrowerId, headers]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const o = data && data.open;
  if (!o) return null;
  const f = o.figures;
  const resolve = async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/income-check/${o.id}/resolve`, { reason }, { headers });
      setReason(''); await load(); if (onChanged) await onChanged();
    } catch (e) { setErr(e?.response?.data?.error || 'Could not resolve it just now.'); } finally { setBusy(false); }
  };
  return (
    <div data-testid="uw-income-check" role="alert" style={card({ gap: 10, border: `2px solid ${T.tan}` })}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Income check open: the decision waits on this</h2>
      <div style={{ fontSize: 14 }}>{o.text}.</div>
      {/* np-step5: the borrower is asked first (a step-5 pay-stub request); resolving here is the fallback. */}
      {o.borrowerAsked && (
        <div data-testid="uw-income-check-asked" style={{ fontSize: 13.5 }}>
          {o.borrowerAsked.status === 'responded'
            ? 'The borrower uploaded a new pay stub on step 5; the check re-runs once it is read.'
            : 'The borrower has been asked for their most recent pay stub on step 5. Resolve here only if they can’t fix it, or the figure stands.'}
        </div>
      )}
      {f && (
        <div data-testid="uw-income-check-figures" style={{ fontSize: 14, lineHeight: 1.7 }}>
          <div>Figure on the file (the documents’ analysis, as the borrower confirmed it): <strong>{money(f.onFile)}</strong> / month</div>
          <div>Cross-check ({f.method}): <strong>{money(f.crossCheck)}</strong> / month</div>
          <div>Difference: <strong>{f.variancePct}%</strong> (tolerance {f.tolerancePct}%)</div>
        </div>
      )}
      {o.resolvable ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 700 }} htmlFor="uw-income-check-reason">Why the figure on the file stands (at least 10 characters)</label>
          <textarea id="uw-income-check-reason" data-testid="uw-income-check-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            style={{ fontSize: 14, padding: 8, borderRadius: 6, border: `1px solid ${T.line}`, fontFamily: T.font }} />
          <div>
            <button type="button" data-testid="uw-income-check-resolve" disabled={busy || reason.trim().length < 10} onClick={resolve}
              style={{ padding: '6px 14px', fontWeight: 700, borderRadius: 6, border: `1px solid ${T.blue}`, background: T.blue, color: T.white, cursor: busy ? 'default' : 'pointer' }}>
              {busy ? 'Saving\u2026' : 'The figure on the file stands'}
            </button>
          </div>
          <div style={{ fontSize: 12.5 }}>If the figure is wrong, correct the documents or the staff income rows instead: the check re-runs and clears itself once it passes.</div>
        </div>
      ) : (
        <div style={{ fontSize: 13 }}>This clears itself once its cause is fixed and the check passes.</div>
      )}
      {err && <div role="alert" style={{ fontSize: 13, color: T.red }}>{err}</div>}
    </div>
  );
}

export function GuidedIncomeStep({ API, borrowerId, headers, flags, onChanged, refreshKey = 0, dispute = null, items = null, itemsOpen = 0, comparison = null }) {
  const { figs, reload } = useFigures(API, borrowerId, headers, refreshKey);
  const [change, setChange] = useState(null);   // { amounts: { i: '' }, reason: '' }
  const [err, setErr] = useState(null);
  const inc = figs && figs.income;
  const open = (flags || []).filter((f) => f.module === 'income');
  const changed = async () => { await reload(); if (onChanged) await onChanged(); };
  if (!figs) return <div style={{ fontSize: 14 }}>Loading…</div>;
  if (!inc) return <div role="alert" style={{ fontSize: 14 }}>The income figures could not be read just now.</div>;
  const counted = inc.override ? inc.override.monthly : inc.qualifyingMonthly;
  const verifiedLines = inc.lines.filter((l) => l.status === 'verified');
  const accept = async () => {
    setErr(null);
    try { await axios.post(`${API}/api/review/admin/${borrowerId}/section`, { section: 'income', action: 'accept' }, { headers }); await changed(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not accept just now.'); }
  };
  const saveChange = async () => {
    setErr(null);
    const total = verifiedLines.reduce((t, l, i) => t + (change.amounts[i] !== '' && change.amounts[i] != null ? Number(String(change.amounts[i]).replace(/[$,\s]/g, '')) : Number(l.monthly) || 0), 0);
    const detail = verifiedLines.map((l, i) => `${l.label}: ${money(change.amounts[i] !== '' && change.amounts[i] != null ? Number(String(change.amounts[i]).replace(/[$,\s]/g, '')) : l.monthly)}/mo`).join('; ');
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/step6-override`, { income: Math.round(total * 100) / 100, reason: `${change.reason.trim()} (${detail})` }, { headers });
      setChange(null); await changed();
    } catch (e) { setErr(e?.response?.data?.error || 'Could not save the figure just now.'); }
  };
  return (
    <div data-testid="uw-guided-income" style={{ display: 'grid', gap: 20, fontFamily: T.font, color: T.ink }}>
      <Headline testid="uw-income-qualifying" title="Qualifying monthly income" figure={<>{money(counted)}<span style={{ fontSize: 18, fontWeight: 600 }}> / month</span></>}
        sub={inc.override ? `Changed by staff${inc.override.at ? ` on ${day(inc.override.at)}` : ''}: ${inc.override.notes || ''}` : `From ${verifiedLines.length} source${verifiedLines.length === 1 ? '' : 's'}, ${(() => { const n = new Set(verifiedLines.flatMap((l) => l.documents.map((d) => d.id))).size; return `${n} document${n === 1 ? '' : 's'}`; })()}. Computed by the income rules for each source; the check’s own figure is never used.`}>
        <StatusCard accepted={inc.accepted} acceptedAt={inc.acceptedAt} status={inc.status} blockedBy={open.length ? `Resolve the open income flag${open.length === 1 ? '' : 's'} first.` : itemsOpen > 0 ? `${itemsOpen} item${itemsOpen === 1 ? '' : 's'} open on this step — resolve ${itemsOpen === 1 ? 'it' : 'them'} first.` : null} />
      </Headline>

      {/* The borrower's open dispute, when there is one (GuidedModuleActions renders nothing otherwise). */}
      {dispute}

      {/* 🔑 THE INCOME CHECK (Jesse 2026-11-01): an open item that holds the decision, resolved here. Staff only. */}
      <IncomeCheckPanel API={API} borrowerId={borrowerId} headers={headers} refreshKey={refreshKey} onChanged={changed} />

      <div data-testid="uw-income-breakdown" style={card({ gap: 16 })}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>How was this calculated</h2>
          <div style={{ fontSize: 13 }}>Income rules · per source{inc.checkedAt ? ` · checked ${dayTime(inc.checkedAt)}` : ''}</div>
        </div>
        {!inc.lines.length && <div style={{ fontSize: 14 }}>No income has been computed from the documents yet.</div>}
        {inc.lines.map((l, i) => (
          <div key={`${l.type}-${i}`} data-testid="uw-income-source" data-type={l.type} style={{ border: `1px solid ${T.line}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '14px 16px', background: T.ground }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>{l.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{l.status === 'verified' ? `${money(l.monthly)} / mo` : 'Not counted'}</div>
            </div>
            <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
              {l.rule && <div data-testid="uw-income-rule"><b>Rule:</b> {l.rule}</div>}
              {l.inputs.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                  {l.inputs.map((x, k) => (
                    <div key={k} style={{ background: T.ground, borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{x.label}{x.taxYear ? ` ${x.taxYear}` : ''}{x.periodEnd ? ` · ${x.periodStart ? `${day(x.periodStart)}–` : 'to '}${day(x.periodEnd)}` : ''}</div>
                      <div style={{ fontWeight: 700 }}>{money(x.amount)}</div>
                    </div>
                  ))}
                </div>
              )}
              {l.steps.length > 0 && <div data-testid="uw-income-steps"><b>Counted:</b> {l.steps.join('; ')}</div>}
              {l.declining && <div><b>Declining:</b> year to date is below the prior year — the lower figure is used.</div>}
              {l.excludedBecause && <div><b>Not counted:</b> {l.excludedBecause}</div>}
              {l.documents.length > 0 && <div style={{ fontSize: 13 }}>Documents: {l.documents.map((d) => d.name || `#${d.id}`).join(' · ')}</div>}
            </div>
          </div>
        ))}
        {inc.stated.length > 0 && (
          <div style={{ fontSize: 13 }}><b>Stated, not verified:</b> {inc.stated.map((s) => `${s.label} (${s.reason})`).join('; ')}</div>
        )}
        {/* 🔑 UNUSUAL DEPOSITS (Jesse, 2026-10-29 — backend utils/depositSourcing): each one, its status and why. The
            borrower may add a document for one; it is optional and never a request. */}
        {inc.depositSourcing && (inc.depositSourcing.deposits.length > 0 || (inc.depositSourcing.review || []).length > 0 || inc.depositSourcing.notCountedYet
          || (inc.depositSourcing.assetsOnly || []).length > 0 || (inc.depositSourcing.olderMonths || []).length > 0 || (inc.depositSourcing.businessAccounts || []).length > 1) && (
          <div data-testid="uw-income-deposits" style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: '14px 16px', display: 'grid', gap: 8, fontSize: 14 }}>
            <div style={{ fontWeight: 800 }}>Deposits</div>
            {inc.depositSourcing.notCountedYet && <div data-testid="uw-income-deposits-short" style={{ fontWeight: 700 }}>{inc.depositSourcing.notCountedYet}</div>}
            {/* 🔑 WHICH STATEMENTS COUNT (afmMatrix BANK_STATEMENT_ACCOUNTS, 2026-10-29). */}
            {(inc.depositSourcing.assetsOnly || []).length > 0 && inc.depositSourcing.incomeAccount && (
              <div data-testid="uw-income-account" style={{ fontSize: 13 }}><b>Income account:</b> {inc.depositSourcing.incomeAccount.label} ({inc.depositSourcing.incomeAccount.months} months). <b>Assets only, not income:</b> {inc.depositSourcing.assetsOnly.map((a) => `${a.label} (${a.months} months)`).join('; ')}.</div>
            )}
            {(inc.depositSourcing.businessAccounts || []).length > 1 && (
              <div data-testid="uw-income-business-accounts" style={{ fontSize: 13 }}><b>Business accounts combined:</b> {inc.depositSourcing.businessAccounts.map((a) => a.label).join('; ')}. Transfers matched between them: {inc.depositSourcing.matchedTransfers}.</div>
            )}
            {(inc.depositSourcing.olderMonths || []).length > 0 && (
              <div data-testid="uw-income-older-months" style={{ fontSize: 13 }}><b>Months counted:</b> {inc.depositSourcing.monthsUsed[0]} to {inc.depositSourcing.monthsUsed[inc.depositSourcing.monthsUsed.length - 1]}. <b>Older, not used:</b> {inc.depositSourcing.olderMonths.join(', ')}.</div>
            )}
            <div style={{ fontSize: 13 }}>{inc.depositSourcing.rule} Average month {money(inc.depositSourcing.averageMonthly)}; threshold {money(inc.depositSourcing.threshold)}.</div>
            {inc.depositSourcing.deposits.map((d) => (
              <div key={d.key} data-testid="uw-income-deposit" data-status={d.status} style={{ background: T.ground, borderRadius: 8, padding: '10px 12px', display: 'grid', gap: 4 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 700 }}>{money(d.amount)} on {d.date ? day(`${d.date}T12:00:00`) : 'an unknown date'}{d.memo ? ` · ${d.memo}` : ''}</span>
                  <span data-testid="uw-income-deposit-status" style={{ fontWeight: 700 }}>{d.statusLabel}</span>
                </div>
                <div style={{ fontSize: 13 }}>{d.reason}</div>
                {d.status !== 'excluded' && <div style={{ fontSize: 13 }}>{d.sourceReason}</div>}
              </div>
            ))}
            {/* 🔑 Business statements (rev 91): a large customer deposit COUNTS and is listed here for staff review only. */}
            {(inc.depositSourcing.review || []).map((d) => (
              <div key={`review-${d.key}`} data-testid="uw-income-deposit-review" style={{ background: T.ground, borderRadius: 8, padding: '10px 12px', display: 'grid', gap: 4 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 700 }}>{money(d.amount)} on {d.date ? day(`${d.date}T12:00:00`) : 'an unknown date'}{d.memo ? ` · ${d.memo}` : ''}</span>
                  <span style={{ fontWeight: 700 }}>For review</span>
                </div>
                <div style={{ fontSize: 13 }}>{d.reason}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 🔑 With / without bank-statement income (push C item 5 — BankStatementComparison; nothing for other files). */}
      {comparison}

      {items}
      <FlagList API={API} borrowerId={borrowerId} headers={headers} flags={flags} module="income" onChanged={changed} />

      {err && <div role="alert" style={{ fontSize: 14, fontWeight: 700 }}>{err}</div>}
      {change ? (
        <div data-testid="uw-income-change" style={card()}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Change the figure</h2>
          {verifiedLines.map((l, i) => (
            <label key={i} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: 14 }}>
              <span style={{ flex: '1 1 260px', minWidth: 0 }}>{l.label} <span style={{ fontSize: 12 }}>(computed {money(l.monthly)} / mo)</span></span>
              <input data-testid="uw-income-change-amount" inputMode="decimal" placeholder={String(l.monthly)} value={change.amounts[i] ?? ''}
                onChange={(e) => setChange((c) => ({ ...c, amounts: { ...c.amounts, [i]: e.target.value } }))} style={{ ...input, width: 160 }} />
            </label>
          ))}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, fontWeight: 600 }}>Reason (recorded on the file)
            <textarea data-testid="uw-income-change-reason" rows={2} value={change.reason} onChange={(e) => setChange((c) => ({ ...c, reason: e.target.value }))}
              style={{ ...input, minHeight: 64, padding: 10, fontWeight: 500 }} />
          </label>
          <div style={{ fontSize: 14 }}>The borrower will be asked to confirm the new figure at step 6.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" data-testid="uw-income-change-save" disabled={change.reason.trim().length < 10} onClick={saveChange} style={btn('primary', change.reason.trim().length < 10)}>Save and reopen step 6</button>
            <button type="button" onClick={() => setChange(null)} style={btn()}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={card({ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 })}>
          <div style={{ fontSize: 14, maxWidth: 560, lineHeight: 1.6 }}>The borrower will be asked to confirm the new figure at step 6.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" data-testid="uw-income-change-open" onClick={() => setChange({ amounts: {}, reason: '' })} style={btn()}>Change the figure</button>
            <button type="button" data-testid="uw-income-accept" disabled={inc.accepted || open.length > 0 || itemsOpen > 0} onClick={accept} style={btn('primary', inc.accepted || open.length > 0 || itemsOpen > 0)}>{inc.accepted ? 'Income accepted' : 'Accept income'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The guided Assets step. */
export function GuidedAssetsStep({ API, borrowerId, headers, flags, onChanged, refreshKey = 0, dispute = null, items = null, itemsOpen = 0 }) {
  const { figs, reload } = useFigures(API, borrowerId, headers, refreshKey);
  const [change, setChange] = useState(null);   // { amounts: { id: '' }, reason: '' }
  const [err, setErr] = useState(null);
  const as = figs && figs.assets;
  const open = (flags || []).filter((f) => f.module === 'assets');
  const changed = async () => { await reload(); if (onChanged) await onChanged(); };
  if (!figs) return <div style={{ fontSize: 14 }}>Loading…</div>;
  if (!as) return <div role="alert" style={{ fontSize: 14 }}>The account figures could not be read just now.</div>;
  const unverified = as.lines.filter((l) => l.verified == null);
  const approve = async () => {
    setErr(null);
    const amounts = {};
    for (const l of as.lines) amounts[l.id] = l.verified != null ? l.verified : 0;
    try { await axios.post(`${API}/api/review/admin/${borrowerId}/section`, { section: 'assets', action: 'accept', amounts }, { headers }); await changed(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not approve just now.'); }
  };
  const saveChange = async () => {
    setErr(null);
    const amounts = {};
    for (const l of as.lines) amounts[l.id] = change.amounts[l.id] !== '' && change.amounts[l.id] != null ? change.amounts[l.id] : (l.verified != null ? l.verified : '');
    try { await axios.post(`${API}/api/admin/borrowers/${borrowerId}/asset-figures`, { amounts, reason: change.reason }, { headers }); setChange(null); await changed(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not save the figures just now.'); }
  };
  return (
    <div data-testid="uw-guided-assets" style={{ display: 'grid', gap: 20, fontFamily: T.font, color: T.ink }}>
      <Headline testid="uw-assets-qualifying" title="Qualifying liquid assets" figure={money(as.qualifying)}
        sub={<>Verified from statements dated within 60 days, in the borrower&rsquo;s name. <span data-testid="uw-assets-planned">Borrower planned to use {money(as.planned)}</span> (information only).</>}>
        <StatusCard accepted={as.accepted} acceptedAt={as.acceptedAt} status={as.status}
          blockedBy={as.lines.some((l) => l.reconfirm) ? 'A newer statement arrived — re-confirm it below.' : open.length ? `Resolve the open assets flag${open.length === 1 ? '' : 's'} first.` : itemsOpen > 0 ? `${itemsOpen} item${itemsOpen === 1 ? '' : 's'} open on this step — resolve ${itemsOpen === 1 ? 'it' : 'them'} first.` : null} />
      </Headline>

      {dispute}

      <div data-testid="uw-assets-breakdown" style={card({ gap: 14 })}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>How was this calculated</h2>
          <div style={{ fontSize: 13 }}>One line per source · the verified figure is what counts</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 720 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: `2px solid ${T.ink}` }}>
                <th scope="col" style={{ padding: '10px 8px' }}>Source</th>
                <th scope="col" style={{ padding: '10px 8px' }}>Statement</th>
                <th scope="col" style={{ padding: '10px 8px' }}>Borrower planned</th>
                <th scope="col" style={{ padding: '10px 8px', textAlign: 'right' }}>Verified</th>
              </tr>
            </thead>
            <tbody>
              {as.lines.map((l) => (
                <tr key={l.id} data-testid="uw-assets-line" data-source={l.id} style={{ borderBottom: `1px solid ${T.line}`, background: l.reconfirm ? T.tan : 'transparent' }}>
                  <td style={{ padding: '12px 8px' }}><b data-testid="uw-assets-line-label">{l.label}</b>{l.reconfirm ? <div style={{ fontSize: 12 }}>newer statement</div> : null}</td>
                  <td style={{ padding: '12px 8px' }}>{l.balanceUnread ? <b data-testid="uw-assets-line-balance-unread">Read, no balance found</b> : l.statementDate ? day(l.statementDate) : 'Not read yet'}</td>
                  <td style={{ padding: '12px 8px' }}>{money(l.planned)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                    {change ? (
                      <input data-testid="uw-assets-change-amount" inputMode="decimal" placeholder={l.verified != null ? String(l.verified) : 'amount'} value={change.amounts[l.id] ?? ''}
                        onChange={(e) => setChange((c) => ({ ...c, amounts: { ...c.amounts, [l.id]: e.target.value } }))} style={{ ...input, width: 140, textAlign: 'right' }} />
                    ) : (
                      <>
                        <b data-testid="uw-assets-line-verified">{l.verified != null ? money(l.verified) : '—'}</b>
                        {l.reconfirm ? <div style={{ fontSize: 12 }}>you confirmed {money(l.reconfirm.staffFigure)} · the lower counts</div> : null}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} style={{ padding: '12px 8px', fontWeight: 800 }}>Qualifying liquid assets</td>
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 800, fontSize: 16 }}>{money(as.qualifying)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 13 }}>A gift or sale proceeds appear here only once verified (letter and deposit, or the settlement statement).</div>
        {/* 🔑 A statement read with no balance (2026-10-26): flagged for staff; the borrower is asked for a clearer copy. */}
        {as.lines.some((l) => l.balanceUnread) && (
          <div data-testid="uw-assets-balance-unread" role="status" style={{ fontSize: 13, background: T.tan, borderRadius: 8, padding: '10px 12px' }}>
            <b>No balance on the statement:</b> {as.lines.filter((l) => l.balanceUnread).map((l) => l.label).join(', ')} — the check read the statement but found no balance. The borrower is asked for a clearer copy on step 5; or enter the figure with &ldquo;Change a figure&rdquo;.
          </div>
        )}
        {unverified.length > 0 && !change && <div style={{ fontSize: 13 }}><b>No balance read:</b> {unverified.map((l) => l.label).join(', ')} — enter the figure with &ldquo;Change a figure&rdquo;, or approve and it counts as $0.</div>}
      </div>

      <DocumentConfirms API={API} borrowerId={borrowerId} headers={headers} module="assets" onChanged={changed} />
      {items}
      <FlagList API={API} borrowerId={borrowerId} headers={headers} flags={flags} module="assets" onChanged={changed} />

      {err && <div role="alert" style={{ fontSize: 14, fontWeight: 700 }}>{err}</div>}
      {change ? (
        <div data-testid="uw-assets-change" style={card()}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, fontWeight: 600 }}>Reason (recorded on the file)
            <textarea data-testid="uw-assets-change-reason" rows={2} value={change.reason} onChange={(e) => setChange((c) => ({ ...c, reason: e.target.value }))}
              style={{ ...input, minHeight: 64, padding: 10, fontWeight: 500 }} />
          </label>
          <div style={{ fontSize: 14 }}>The borrower will be asked to confirm the new figures at step 6.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" data-testid="uw-assets-change-save" disabled={change.reason.trim().length < 10} onClick={saveChange} style={btn('primary', change.reason.trim().length < 10)}>Save and reopen step 6</button>
            <button type="button" onClick={() => setChange(null)} style={btn()}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={card({ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 })}>
          <div style={{ fontSize: 14, maxWidth: 560, lineHeight: 1.6 }}>Changing a figure needs a reason. The borrower will be asked to confirm the new figure at step 6.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" data-testid="uw-assets-change-open" onClick={() => setChange({ amounts: {}, reason: '' })} style={btn()}>Change a figure</button>
            <button type="button" data-testid="uw-assets-approve" disabled={(as.accepted && !as.lines.some((l) => l.reconfirm)) || open.length > 0 || itemsOpen > 0} onClick={approve}
              style={btn('primary', (as.accepted && !as.lines.some((l) => l.reconfirm)) || open.length > 0 || itemsOpen > 0)}>{as.accepted && !as.lines.some((l) => l.reconfirm) ? 'Assets approved' : `Approve ${money(as.qualifying)}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Each step's open requests, explanations and letters (GET /api/admin/borrowers/:id/module-items — backend utils/moduleItems). */
export function useModuleItems(API, borrowerId, headers, refreshKey = 0) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    if (!borrowerId) return;
    try { const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/module-items`, { headers }); setData(r.data); }
    catch (_) { setData({ byModule: {}, counts: {} }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, borrowerId, refreshKey]);
  useEffect(() => { load(); }, [load]);
  // 🔑 counts = what the strip shows (the step's own items + the file's blockers on it — backend utils/fileBlockers, push C
  // item 1); acceptCounts = what holds the step's Accept (the server's STEP_ITEMS_OPEN rule, unchanged).
  return { byModule: (data && data.byModule) || {}, counts: (data && data.counts) || {}, acceptCounts: (data && (data.acceptCounts || data.counts)) || {},
    reviewOpen: (data && data.reviewOpen) || [], reload: load, loaded: data !== null };
}

/**
 * 🔑 A STEP'S OWN OPEN ITEMS (Jesse, 2026-10-26): the borrower's open requests (Upload / Dismiss with a reason), their
 * written explanations (Accept and close / Ask for the document) and, on Credit, the letters of explanation. The step's
 * Accept stays off while any is open (and the server refuses it — routes/review.js STEP_ITEMS_OPEN).
 */
export function StepItems({ API, borrowerId, headers, items, module, onChanged }) {
  const m = (items && items.byModule && items.byModule[module]) || { needs: [], explanations: [], letters: [] };
  const blockers = m.blockers || [];
  const [err, setErr] = useState(null);
  const [dismiss, setDismiss] = useState({});
  const fileRef = React.useRef({});
  if (!m.needs.length && !m.explanations.length && !m.letters.length && !blockers.length) return null;
  const run = async (fn) => { setErr(null); try { await fn(); if (onChanged) await onChanged(); } catch (e) { setErr(e?.response?.data?.error || 'That could not be saved just now.'); } };
  const upload = (n, files) => run(async () => {
    if (!files || !files.length) return;
    const fd = new FormData(); fd.append('need_id', String(n.id)); for (const f of files) fd.append('files', f);
    await axios.post(`${API}/api/admin/borrowers/${n.userId || borrowerId}/upload-need-document`, fd, { headers });
  });
  return (
    <div data-testid={`uw-step-items-${module}`} style={{ ...card(), fontFamily: T.font, color: T.ink }}>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Open on this step · {m.needs.length + m.explanations.length + m.letters.length + blockers.length}</h2>
      {err && <div role="alert" style={{ fontSize: 13, fontWeight: 700 }}>{err}</div>}
      {/* 🔑 THE FILE'S BLOCKERS ON THIS STEP (push C item 1 — backend utils/fileBlockers): the same lines the status card
          and step 8 show, in their order. Each is cleared where it always was (the VOM panel, the hold, the decision). */}
      {blockers.map((b) => (
        <div key={`b${b.code}`} data-testid="uw-step-blocker" data-code={b.code || ''} data-owner={b.owner} style={{ borderTop: `1px solid ${T.line}`, paddingTop: 12, fontSize: 14 }}>
          {b.line}{b.action ? <span style={{ fontSize: 12 }}> · {b.action}</span> : null}
        </div>
      ))}
      {m.needs.map((n) => (
        <div key={`n${n.id}`} data-testid="uw-step-need" style={{ borderTop: `1px solid ${T.line}`, paddingTop: 12, display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 14 }}><b>Waiting on borrower:</b> {n.text} <span style={{ fontSize: 12 }}>(need #{n.id})</span></div>
          {!dismiss[n.id] ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="file" multiple aria-label={`Upload for need ${n.id}`} style={{ display: 'none' }} ref={(el) => { fileRef.current[n.id] = el; }} onChange={(e) => upload(n, e.target.files)} />
              <button type="button" data-testid="uw-step-need-upload" onClick={() => fileRef.current[n.id] && fileRef.current[n.id].click()} style={btn('primary')}>Upload what they sent</button>
              <button type="button" data-testid="uw-step-need-dismiss" onClick={() => setDismiss((d) => ({ ...d, [n.id]: '' }))} style={btn()}>Dismiss with a reason</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input aria-label="Why" placeholder="Why it is no longer needed (recorded on the file)" value={dismiss[n.id]} onChange={(e) => setDismiss((d) => ({ ...d, [n.id]: e.target.value }))}
                style={{ minHeight: 40, flex: '1 1 260px', borderRadius: 8, border: `1px solid ${T.ink}`, padding: '0 10px', font: `500 14px ${T.font}` }} />
              <button type="button" data-testid="uw-step-need-dismiss-confirm" disabled={String(dismiss[n.id]).trim().length < 10}
                onClick={() => run(async () => { await axios.patch(`${API}/api/admin/borrowers/${n.userId || borrowerId}/additional-needs/${n.id}`, { status: 'dismissed', reason: dismiss[n.id] }, { headers }); setDismiss((d) => { const x = { ...d }; delete x[n.id]; return x; }); })}
                style={btn('primary', String(dismiss[n.id]).trim().length < 10)}>Dismiss</button>
              <button type="button" onClick={() => setDismiss((d) => { const x = { ...d }; delete x[n.id]; return x; })} style={btn()}>Cancel</button>
            </div>
          )}
        </div>
      ))}
      {m.explanations.map((x) => (
        <div key={`e${x.id}`} data-testid="uw-explanation" style={{ borderTop: `1px solid ${T.line}`, paddingTop: 12, display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 13 }}><b>Borrower explanation</b> · request: {x.title || x.text}</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, background: T.ground, borderRadius: 8, padding: '10px 12px' }}>&ldquo;{x.explanation}&rdquo;</div>
          {x.respondedAt && <div style={{ fontSize: 12 }}>Written {dayTime(x.respondedAt)}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" data-testid="uw-explanation-accept" onClick={() => run(() => axios.post(`${API}/api/admin/borrowers/${x.userId || borrowerId}/needs/${x.id}/explanation`, { action: 'accept' }, { headers }))} style={btn('primary')}>Accept and close</button>
            <button type="button" data-testid="uw-explanation-ask" onClick={() => run(() => axios.post(`${API}/api/admin/borrowers/${x.userId || borrowerId}/needs/${x.id}/explanation`, { action: 'ask_document' }, { headers }))} style={btn()}>Ask for the document</button>
          </div>
        </div>
      ))}
      {m.letters.map((l) => (
        <div key={`l${l.id}`} data-testid="uw-step-letter" style={{ borderTop: `1px solid ${T.line}`, paddingTop: 12, fontSize: 14 }}>
          <b>{l.written ? 'Letter to review' : 'Waiting on borrower: write the letter'}</b> · {l.title}{l.text ? ` — ${l.text}` : ''} <span style={{ fontSize: 12 }}>(letter #{l.id})</span>
          {l.written && <div style={{ fontSize: 12, marginTop: 4 }}>Accept it, or ask for a new letter, in the letters list on this step.</div>}
        </div>
      ))}
    </div>
  );
}

/** The Docs step: a read-only list of every document on the file. */
// 🔑 RENTALS AND THE CURRENT HOME ON HOUSING (Jesse, 2026-10-26 — backend utils/rentalProperties). One row per property:
// how it counts (income, a negative net, or the full payment), the lease rent, PITIA and its parts, the matched mortgage
// tradeline, the rent proof, the sale documents. "The sale ended" (contract terminated / proceeds didn't arrive) makes the
// payment count and re-decides the file. STAFF ONLY.
const RP_REASON = { income: 'Rent counts (net income)', negative_net: 'Negative net → a monthly debt', no_proof: 'No rent proof — full payment counts',
  no_lease: 'No lease — full payment counts', tenant_not_paid: 'Lease or rent not in yet: full payment counts', kept: 'Kept, not rented — full payment counts',
  sale_not_met: 'Selling — contract and financing not both shown — payment counts', sale_excluded: 'Selling — contract and financing cleared — payment excluded', no_payment: 'Payment not read yet' };
// 🔑 STAFF RESOLVE A CURRENT-HOME CONFLICT (Jesse, 2026-10-27 — backend utils/housingConflict.answer): the same three
// choices as the borrower's future step-5 card, plus where the answer came from (a call, an email or a ticket, with its
// id) and a note. Allow-listed on the server ('housing-conflict'). STAFF ONLY.
const CONFLICT_CHOICES = [['current_home', 'It\u2019s the mortgage on the current home'], ['other_property', 'It\u2019s a loan on a different property'], ['something_else', 'Something else']];
function ResolveConflict({ API, borrowerId, headers, conflict, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ choice: '', address: '', source: '', sourceRef: '', note: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const ready = f.choice && f.source && f.sourceRef.trim() && f.note.trim().length >= 10 && (f.choice !== 'other_property' || f.address.trim().length >= 6);
  const save = async () => {
    setBusy(true); setErr(null);
    try { await axios.post(`${API}/api/admin/borrowers/${borrowerId}/housing-conflicts/${conflict.id}/resolve`, f, { headers }); setOpen(false); await onDone(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not save that.'); }
    finally { setBusy(false); }
  };
  if (!open) return <button type="button" data-testid="uw-housing-conflict-resolve" style={{ ...btn(), justifySelf: 'start', marginTop: 6 }} onClick={() => setOpen(true)}>Resolve conflict</button>;
  return (
    <div data-testid="uw-housing-conflict-form" style={{ display: 'grid', gap: 8, marginTop: 8 }}>
      {CONFLICT_CHOICES.map(([v, label]) => (
        <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="radio" name={`hc-${conflict.id}`} data-testid={`uw-housing-conflict-choice-${v}`} checked={f.choice === v} onChange={() => setF((p) => ({ ...p, choice: v }))} />{label}
        </label>
      ))}
      {f.choice === 'other_property' && (
        <label style={{ fontSize: 12, fontWeight: 700 }}>That property&rsquo;s address (added as an owned property; its mortgage counts in DTI)
          <input data-testid="uw-housing-conflict-address" value={f.address} onChange={(e) => setF((p) => ({ ...p, address: e.target.value }))} style={{ ...input, width: '100%' }} /></label>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, fontWeight: 700 }}>Where it came from
          <select data-testid="uw-housing-conflict-source" value={f.source} onChange={(e) => setF((p) => ({ ...p, source: e.target.value }))} style={{ ...input, display: 'block' }}>
            <option value="">Choose…</option><option value="call">Call</option><option value="email">Email</option><option value="ticket">Ticket</option>
          </select></label>
        <label style={{ fontSize: 12, fontWeight: 700, flex: '1 1 180px' }}>Call, email or ticket reference
          <input data-testid="uw-housing-conflict-ref" value={f.sourceRef} onChange={(e) => setF((p) => ({ ...p, sourceRef: e.target.value }))} style={{ ...input, width: '100%' }} /></label>
      </div>
      <label style={{ fontSize: 12, fontWeight: 700 }}>Note (what the borrower said)
        <textarea data-testid="uw-housing-conflict-note" rows={2} value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} style={{ ...input, width: '100%', minHeight: 56, padding: 8 }} /></label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" data-testid="uw-housing-conflict-save" style={btn('primary', !ready || busy)} disabled={!ready || busy} onClick={save}>{busy ? 'Saving…' : 'Save the answer'}</button>
        <button type="button" style={btn()} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      {err && <div role="alert" style={{ fontSize: 13 }}>{err}</div>}
    </div>
  );
}
// 🔑 THE VOM CONTROLS FOR STAFF (Jesse, 2026-10-29 — the approved Module mockups 9a–9f; backend utils/vomSends): every send
// with who sent it; Resend / Edit and resend under the borrower's own rules (ONE clock, ONE live link — the old link is
// cancelled only once the new email has gone); "Override the 24-hour limit" with a required note; Lock VOM changes with a
// required note (no Resend / Edit for the borrowers or staff until Unlock); and F: "Record a VOM received by email" — the
// VOM and its figures complete the request like the lender's own upload (the checks, escrow, the hold released).
const sendWhat = { first: 'sent to', resend: 'resent to', edit: 'edited and sent to' };
function VomStaffControls({ API, borrowerId, headers, v, onDone, canConfirm = false }) {
  const [mode, setMode] = useState(null);   // 'override' | 'edit' | 'lock' | 'record'
  const [f, setF] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const outstanding = (v.status || 'pending') === 'pending' && !v.statementRouteAt;
  const sends = Array.isArray(v.sends) ? v.sends : [];
  const lim = v.limits || { canResend: true, canEdit: true };
  const lock = v.lock || null;
  const set = (k) => (e) => { const val = e.target.value; setF((p) => ({ ...p, [k]: val })); setErr(null); };
  const open = (m, init = {}) => { setMode(m); setF(init); setFile(null); setErr(null); };
  const post = async (url, body, opts = {}) => {
    setBusy(true); setErr(null);
    try { await axios.post(`${API}/api/admin/borrowers/${borrowerId}${url}`, body, { headers, ...opts }); setMode(null); await onDone(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not save that.'); }
    finally { setBusy(false); }
  };
  const noteOk = String(f.note || '').trim().length >= 10;
  const field = (k, l, type = 'text') => (
    <label style={{ fontSize: 12, fontWeight: 700 }}>{l}<input type={type} data-testid={`uw-vom-${mode}-${k}`} value={f[k] || ''} onChange={set(k)} style={{ ...input, display: 'block', width: '100%' }} /></label>);
  const note = (l) => (
    <label style={{ fontSize: 12, fontWeight: 700 }}>{l}<textarea data-testid={`uw-vom-${mode}-note`} rows={2} value={f.note || ''} onChange={set('note')} style={{ ...input, width: '100%', minHeight: 56, padding: 8 }} /></label>);
  const actions = (label, ready, go) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <button type="button" data-testid={`uw-vom-${mode}-save`} style={btn('primary', !ready || busy)} disabled={!ready || busy} onClick={go}>{busy ? 'Saving…' : label}</button>
      <button type="button" style={btn()} onClick={() => setMode(null)}>Cancel</button>
    </div>);
  const payOk = Number(String(f.payment || '').replace(/[$,\s]/g, '')) > 0;
  const recordReady = !!file && payOk && (f.escrowed === 'yes' || f.escrowed === 'no') && noteOk;
  const record = () => {
    const fd = new FormData();
    fd.append('file', file); fd.append('vom_id', String(v.id)); fd.append('monthly_payment', f.payment); fd.append('escrowed', f.escrowed);
    fd.append('lender_name', f.lenderName || ''); fd.append('note', f.note || '');
    return post('/vom/record-email', fd);
  };
  return (
    <div data-testid="uw-vom-controls" data-locked={lock ? 'true' : 'false'} style={{ display: 'grid', gap: 6, marginTop: 4 }}>
      {sends.length > 0 && (
        <div data-testid="uw-vom-sends"><b>Sends</b>
          <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
            {sends.slice().reverse().map((x) => (
              <li key={x.id} data-testid="uw-vom-send" data-kind={x.kind} data-actor={x.actor}>
                {`${dayTime(x.sentAt)} · ${sendWhat[x.kind] || 'sent to'} ${x.lenderEmail} · ${x.actorName || (x.actor === 'staff' ? 'Staff' : 'Borrower')} (${x.actor === 'staff' ? 'staff' : 'borrower'})`}
                {x.overrideNote ? ` · 24-hour limit overridden: “${x.overrideNote}”` : ''}{x.bouncedAt ? ' · bounced' : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {outstanding && v.bouncedAt && <div data-testid="uw-vom-bounced">The email to {v.lenderEmail} bounced{v.bouncedAt ? ` (${dayTime(v.bouncedAt)})` : ''}.</div>}
      {outstanding && v.expired && <div data-testid="uw-vom-expired">The lender&rsquo;s link expired {fmtDay(v.expiresAt) || ''}.</div>}
      {outstanding && lock && (
        <>
          <div data-testid="uw-vom-locked" style={{ fontWeight: 700 }}>VOM changes locked by {lock.lockedByName || 'staff'} · {dayTime(lock.lockedAt)} &mdash; &ldquo;{lock.note}&rdquo;</div>
          <div>Locked: no Resend or Edit and resend for the borrowers or staff. Recorded in the file&rsquo;s history.</div>
          <button type="button" data-testid="uw-vom-unlock" style={{ ...btn('secondary', busy), justifySelf: 'start' }} disabled={busy} onClick={() => post('/vom/unlock', { vom_id: v.id })}>Unlock</button>
        </>
      )}
      {outstanding && !lock && !mode && (
        <>
          {!lim.canResend && lim.resendAt && <div data-testid="uw-vom-clock">One link is live: {v.lenderEmail} ({dayTime(v.sentAt)}). Next resend from {dayTime(lim.resendAt)} &mdash; one clock for the borrowers and staff.</div>}
          {!lim.canEdit && <div data-testid="uw-vom-edits-used">The lender&rsquo;s details were changed 3 times in the last 24 hours.</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" data-testid="uw-vom-resend" style={btn('secondary', !lim.canResend || busy)} disabled={!lim.canResend || busy} onClick={() => post('/vom/resend', { vom_id: v.id })}>Resend</button>
            <button type="button" data-testid="uw-vom-edit" style={btn()} onClick={() => open('edit', { name: v.lenderName || '', email: v.lenderEmail || '', phone: '' })}>Edit and resend</button>
            <button type="button" data-testid="uw-vom-lock" style={btn()} onClick={() => open('lock')}>Lock VOM changes</button>
          </div>
          {(!lim.canResend || !lim.canEdit) && <button type="button" data-testid="uw-vom-override" style={{ ...btn(), justifySelf: 'start' }} onClick={() => open('override')}>Override the 24-hour limit</button>}
        </>
      )}
      {/* V1 (11-01): recording a VOM's figures by email is limited to Hannah and Jesse, like the VOM card's save. */}
      {outstanding && !mode && canConfirm && <button type="button" data-testid="uw-vom-record" style={{ ...btn(), justifySelf: 'start' }} onClick={() => open('record', { lenderName: v.lenderName || '' })}>Record a VOM received by email</button>}
      {mode === 'override' && (
        <div data-testid="uw-vom-override-form" style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          {note('Note (required — why resend inside 24 hours)')}
          {actions('Resend now', noteOk, () => post('/vom/resend', { vom_id: v.id, override_note: f.note }))}
        </div>
      )}
      {mode === 'edit' && (
        <div data-testid="uw-vom-edit-form" style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          {field('name', 'Lender’s name')}{field('email', 'Lender’s email', 'email')}{field('phone', 'Lender’s phone (optional)', 'tel')}
          {note(lim.canEdit ? 'Note (why it changed)' : 'Note (required — the daily limit is reached)')}
          <div>The link {v.lenderName || 'the lender'} has now is cancelled only once the new email has gone. A new address doesn&rsquo;t count toward the 24 hours (3 address changes a day).</div>
          {actions('Send the new request', String(f.name || '').trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(f.email || '').trim()) && (lim.canEdit || noteOk),
            () => post('/request-vom', { vom_id: v.id, lender_name: f.name, lender_email: f.email, lender_phone: f.phone || null, override_note: f.note || null }))}
        </div>
      )}
      {mode === 'lock' && (
        <div data-testid="uw-vom-lock-form" style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          {note('Note (required — why lock)')}
          <div>While locked, the borrowers see the sent line but no Resend or Edit and resend. The 12 months of payments stay open to them.</div>
          {actions('Lock VOM changes', noteOk, () => post('/vom/lock', { vom_id: v.id, note: f.note }))}
        </div>
      )}
      {mode === 'record' && (
        <div data-testid="uw-vom-record-form" style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 700 }}>The VOM (PDF or image)<input type="file" data-testid="uw-vom-record-file" accept="application/pdf,image/*" onChange={(e) => setFile((e.target.files || [])[0] || null)} style={{ display: 'block', fontSize: 12 }} /></label>
          {field('payment', 'Monthly payment on the VOM')}
          <label style={{ fontSize: 12, fontWeight: 700 }}>Taxes and insurance paid through the loan
            <select data-testid="uw-vom-record-escrowed" value={f.escrowed || ''} onChange={set('escrowed')} style={{ ...input, display: 'block' }}>
              <option value="">Choose…</option><option value="yes">Yes</option><option value="no">No</option>
            </select></label>
          {field('lenderName', 'Lender on the VOM')}
          {note('Note (required — who sent it and when)')}
          <div>This completes the VOM like the lender&rsquo;s own upload: the checks run, the lender&rsquo;s link closes and a held decision is released through the normal path.</div>
          {actions('Record the VOM', recordReady, record)}
        </div>
      )}
      {err && <div role="alert" data-testid="uw-vom-controls-error" style={{ fontSize: 13 }}>{err}</div>}
    </div>
  );
}
// 🔑 A PRIVATE LENDER'S VOM, FOR STAFF (Jesse, 2026-10-28 — backend utils/privateVomChecks): the request, the signed
// VOM the lender uploaded, the staff-only checks (the lender vs the payee on the payments; the lender's email / phone /
// address vs a borrower's or the subject property's; the VOM's borrower and property), the reminders, and the
// statement route when the file used it. Any check that didn't match holds the decision until resolved here
// (how it was confirmed + a note). STAFF ONLY — the borrower and the lender never see this.
const VOM_RESOLVE_SOURCES = [['call', 'Call'], ['email', 'Email'], ['document', 'Document'], ['other', 'Other']];
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' }) : null);
function VomStaffCard({ API, borrowerId, headers, v, onDone, canConfirm = false }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ source: '', note: '', payment: '', escrowed: '', total: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // 🔑 CONFIRM VOM FIGURES (P0, Jesse 2026-10-30 — backend utils/vomStaffConfirm): a completed VOM whose payment or escrow
  // answer was not read (or is under an open confirm_ check). The payment / escrow are required exactly then; with escrow
  // yes the total including escrow too. The same route as the check resolve; the decision re-runs once saved.
  // 🔑 AN ESCROWED VOM WITHOUT ITS TOTAL (hotfix, Jesse 2026-10-31): an approved read gave the P&I and "yes" but no total —
  // not a hold (pricing uses the VOM's figure), but the card still asks for the total including escrow: "Confirm VOM figures".
  const raw = v.needs || {};
  const asksTotal = !(v.checkStatus === 'open' || raw.payment || raw.escrow) && v.status === 'completed' && !!raw.total && v.escrowed === true;
  const needs = asksTotal ? { ...raw, action: 'Confirm VOM figures' } : raw;
  const needsFig = !!(needs.payment || needs.escrow);
  const escrowYes = f.escrowed === 'yes' || (!needs.escrow && v.escrowed === true);
  const totalRequired = escrowYes && !(Number(v.totalWithEscrow) > 0);
  const num = (x) => Number(String(x || '').replace(/[$,\s]/g, ''));
  const ready = f.source && f.note.trim().length >= 10 && (!needs.payment || num(f.payment) > 0) && (!needs.escrow || !!f.escrowed) && (!totalRequired || num(f.total) > 0);
  const save = async () => {
    setBusy(true); setErr(null);
    const body = { source: f.source, note: f.note, ...(f.payment ? { monthly_payment: f.payment } : {}), ...(f.escrowed ? { escrowed: f.escrowed } : {}), ...(f.total ? { total_payment_with_escrow: f.total } : {}) };
    try { await axios.post(`${API}/api/admin/borrowers/${borrowerId}/vom-checks/${v.id}/resolve`, body, { headers }); setOpen(false); await onDone(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not save that.'); }
    finally { setBusy(false); }
  };
  const held = v.checkStatus === 'open' || needsFig;
  const asks = held || asksTotal;
  const vomAction = needs.action || (needs.missing ? 'Confirm VOM figures' : 'Resolve VOM check');
  const money = (n) => (n == null ? 'not read' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  return (
    <div data-testid="uw-vom" data-status={v.status || ''} data-check-status={v.checkStatus || ''} role="status"
      style={{ background: held ? T.tan : T.slateLight, borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.5, display: 'grid', gap: 4 }}>
      <b>Private lender&rsquo;s VOM · {v.lenderName}{v.lenderEmail ? ` (${v.lenderEmail})` : ''}</b>
      <div>Sent {fmtDay(v.sentAt) || '—'}{v.reminders && v.reminders.day3 ? ` · reminded ${fmtDay(v.reminders.day3)}` : ''}{v.reminders && v.reminders.day7 ? ` and ${fmtDay(v.reminders.day7)}` : ''}
        {v.status === 'completed' ? ` · signed VOM received ${fmtDay(v.uploadedAt || v.completedAt) || ''}${v.documentId ? ` (document #${v.documentId})` : ''}` : ''}</div>
      {v.statementRouteAt && <div data-testid="uw-vom-statement-route"><b>Statement route:</b> the 12 months of payments to the lender were accepted on {fmtDay(v.statementRouteAt)}. The lender&rsquo;s link is closed.</div>}
      <VomStaffControls API={API} borrowerId={borrowerId} headers={headers} v={v} onDone={onDone} canConfirm={canConfirm} />
      {(v.checks || []).length > 0 && (
        <ul data-testid="uw-vom-checks" style={{ margin: '2px 0 0', paddingLeft: 18 }}>
          {v.checks.map((c) => <li key={c.key} data-testid="uw-vom-check" data-key={c.key} data-ok={c.ok === true ? 'true' : c.ok === false ? 'false' : 'unknown'}>{c.ok === true ? '✓ ' : c.ok === false ? 'Doesn’t match: ' : 'Not compared: '}{c.label}</li>)}
        </ul>
      )}
      {v.status === 'completed' && (
        <div data-testid="uw-vom-figures" data-needs-payment={needs.payment ? 'true' : 'false'} data-needs-escrow={needs.escrow ? 'true' : 'false'}>
          Monthly payment: {money(v.monthlyPayment)} · Taxes and insurance through the loan: {v.escrowed === true ? 'yes' : v.escrowed === false ? 'no' : 'not read'}
          {v.escrowed === true || v.totalWithEscrow != null ? ` · Total including escrow: ${money(v.totalWithEscrow)}` : ''}
        </div>
      )}
      {held && <div data-testid="uw-vom-held" style={{ fontWeight: 700 }}>Waiting on staff — the decision is held until this is resolved.</div>}
      {v.checkStatus === 'resolved' && <div data-testid="uw-vom-resolved">Resolved {fmtDay(v.resolvedAt) || ''}{v.resolutionNote ? ` — “${v.resolutionNote}”` : ''}</div>}
      {/* 🔑 The label follows the real VOM state (push B C3 — backend utils/vomStaffConfirm figuresNeeded().action): a figure
          missing → "Confirm VOM figures"; an uncertain read / staff-only check → "Resolve VOM check" — the same words as
          the status card and step 8's Open items. */}
      {/* 🚨 ONLY THE ALLOW-LISTED STAFF (hotfix 2026-10-31): the route is requireAllowList('vom-check') — constants/privilegedStaff,
          by id. The server says whether this staff member is on it (GET /rentals canConfirmVom); everyone else reads the figures. */}
      {asks && !canConfirm && <div data-testid="uw-vom-confirm-allowlist">Only Hannah or Jesse can confirm VOM figures.</div>}
      {asks && canConfirm && !open && <button type="button" data-testid="uw-vom-resolve" data-action={vomAction} style={{ ...btn(), justifySelf: 'start', marginTop: 6 }} onClick={() => setOpen(true)}>{vomAction}</button>}
      {asks && canConfirm && open && (
        <div data-testid="uw-vom-resolve-form" style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          {needs.payment && (
            <label style={{ fontSize: 12, fontWeight: 700 }}>Monthly payment on the VOM (principal and interest)
              <input data-testid="uw-vom-confirm-payment" inputMode="decimal" value={f.payment} onChange={(e) => setF((p) => ({ ...p, payment: e.target.value }))} style={{ ...input, display: 'block' }} /></label>
          )}
          {/* Shown whenever figures are confirmed — required only when unread or under an open check, but always
              correctable: a VOM completed by the old accept path may carry a stored "no" that was never read (VOM #5). */}
          {(needs.escrow || needsFig) && (
            <label style={{ fontSize: 12, fontWeight: 700 }}>Taxes and insurance paid through the loan (escrow){!needs.escrow && v.escrowed != null ? ` — stored: ${v.escrowed ? 'yes' : 'no'}` : ''}
              <select data-testid="uw-vom-confirm-escrow" value={f.escrowed} onChange={(e) => setF((p) => ({ ...p, escrowed: e.target.value }))} style={{ ...input, display: 'block' }}>
                <option value="">Choose…</option><option value="yes">Yes</option><option value="no">No</option>
              </select></label>
          )}
          {totalRequired && (
            <label style={{ fontSize: 12, fontWeight: 700 }}>Total monthly payment including escrow
              <input data-testid="uw-vom-confirm-total" inputMode="decimal" value={f.total} onChange={(e) => setF((p) => ({ ...p, total: e.target.value }))} style={{ ...input, display: 'block' }} /></label>
          )}
          <label style={{ fontSize: 12, fontWeight: 700 }}>How it was confirmed
            <select data-testid="uw-vom-resolve-source" value={f.source} onChange={(e) => setF((p) => ({ ...p, source: e.target.value }))} style={{ ...input, display: 'block' }}>
              <option value="">Choose…</option>{VOM_RESOLVE_SOURCES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
          <label style={{ fontSize: 12, fontWeight: 700 }}>Note (what you confirmed)
            <textarea data-testid="uw-vom-resolve-note" rows={2} value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} style={{ ...input, width: '100%', minHeight: 56, padding: 8 }} /></label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" data-testid="uw-vom-resolve-save" style={btn('primary', !ready || busy)} disabled={!ready || busy} onClick={save}>{busy ? 'Saving…' : vomAction === 'Confirm VOM figures' ? 'Confirm and release the decision' : 'Resolve and release the decision'}</button>
            <button type="button" style={btn()} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
      {err && <div role="alert" data-testid="uw-vom-error" style={{ fontSize: 13 }}>{err}</div>}
    </div>
  );
}
// 🔑 DOCUMENT FIGURES TO CONFIRM (Jesse, 2026-10-30 — backend utils/documentStaffConfirm): a document whose read figure
// (an account balance, the annual tax, the annual premium, the mortgage payment, the lease rent) came back uncertain.
// Staff enter the figure and a note; the backend writes the type's own field and re-runs the decision. STAFF ONLY.
export function DocumentConfirms({ API, borrowerId, headers, module, onChanged }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try { setList(((await axios.get(`${API}/api/admin/borrowers/${borrowerId}/document-confirms`, { headers })).data.confirms || []).filter((c) => c.module === module)); }
    catch (_) { setList([]); }
  }, [API, borrowerId, headers, module]);
  useEffect(() => { load(); }, [load]);
  if (!list.length) return null;
  const set = (id, k, v) => setF((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));
  const save = async (c) => {
    setBusy(c.id); setErr(null);
    const v = f[c.id] || {};
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/document-confirms/${c.id}`, { values: Object.fromEntries(c.fields.map((x) => [x.key, v[x.key]])), note: v.note || '' }, { headers });
      await load(); if (onChanged) await onChanged();
    } catch (e) { setErr(e?.response?.data?.error || 'Could not save that.'); } finally { setBusy(null); }
  };
  return (
    <div data-testid="uw-doc-confirms" role="status" style={{ background: T.tan, borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.5, display: 'grid', gap: 8 }}>
      <b>Document figures to confirm</b>
      {list.map((c) => {
        const v = f[c.id] || {};
        const ready = c.fields.every((x) => String(v[x.key] || '').trim() !== '') && String(v.note || '').trim().length >= 10;
        return (
          <div key={c.id} data-testid="uw-doc-confirm" data-doc-type={c.documentType} data-document-id={c.documentId} style={{ display: 'grid', gap: 6 }}>
            <div>{c.label}{c.fileName ? ` · ${c.fileName}` : ''} (document #{c.documentId}) — the read did not give a certain {c.fields.map((x) => x.label).join(' and ')}.</div>
            {c.fields.map((x) => (
              <label key={x.key} style={{ fontSize: 12, fontWeight: 700 }}>{x.label.charAt(0).toUpperCase() + x.label.slice(1)}{x.read != null ? ` (read: ${x.read})` : ''}
                <input data-testid={`uw-doc-confirm-${x.key}`} inputMode="decimal" value={v[x.key] || ''} onChange={(e) => set(c.id, x.key, e.target.value)} style={{ ...input, display: 'block' }} /></label>
            ))}
            <label style={{ fontSize: 12, fontWeight: 700 }}>Note (where the figure came from)
              <textarea data-testid="uw-doc-confirm-note" rows={2} value={v.note || ''} onChange={(e) => set(c.id, 'note', e.target.value)} style={{ ...input, width: '100%', minHeight: 56, padding: 8 }} /></label>
            <button type="button" data-testid="uw-doc-confirm-save" style={{ ...btn('primary', !ready || busy === c.id), justifySelf: 'start' }} disabled={!ready || busy === c.id} onClick={() => save(c)}>
              {busy === c.id ? 'Saving…' : 'Confirm and re-run the decision'}</button>
          </div>
        );
      })}
      {err && <div role="alert" style={{ fontSize: 13 }}>{err}</div>}
    </div>
  );
}
// 🔑 STAFF ADD A PRIVATE LENDER'S HOME'S TAX STATEMENT / INSURANCE DECLARATIONS PAGE (2026-10-27), until the borrower's own
// rows ship: the existing staff upload (read by the same extraction), then attached to the property. STAFF ONLY.
function StaffPropertyUpload({ API, borrowerId, headers, propertyId, role, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', role === 'tax_bill' ? 'property_tax_bill' : 'insurance_declarations');
      const up = await axios.post(`${API}/api/admin/borrowers/${borrowerId}/upload-document`, fd, { headers });
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/rentals/${propertyId}/attach`, { documentId: up.data.document_id, role }, { headers });
      await onDone();
    } catch (e) { setErr(e?.response?.data?.error || 'Could not upload that.'); }
    finally { setBusy(false); }
  };
  return (
    <label data-testid={`uw-rental-staff-upload-${role}`} style={{ fontSize: 12, fontWeight: 700, display: 'grid', gap: 4 }}>
      {busy ? 'Uploading…' : `Upload the ${label} for the borrower`}
      <input type="file" disabled={busy} onChange={(e) => upload(e.target.files && e.target.files[0])} style={{ fontSize: 12 }} />
      {err && <span role="alert" style={{ fontWeight: 400 }}>{err}</span>}
    </label>
  );
}

// 🔑 STAFF ADD A LEASE OR A RENT / DEPOSIT PROOF — THE PROPERTY IS A REQUIRED CHOICE (Jesse, 2026-10-28; backend
// utils/rentalProperties.staffPropertyChoice). No property is preselected, even when there is only one; the server
// refuses the upload without it and attaches the document to the chosen property's row. STAFF ONLY.
const STAFF_PROPERTY_TYPES = [['lease', 'Lease'], ['rental_deposit_statements', 'Rent / deposit proof (bank statement)']];
function StaffLeaseUpload({ API, borrowerId, headers, properties, onDone }) {
  const [propertyId, setPropertyId] = useState('');
  const [docType, setDocType] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const rentable = (properties || []).filter((p) => p.kind !== 'owned_other');
  const ready = !!propertyId && !!docType && !busy;
  const upload = async (file) => {
    if (!file || !propertyId || !docType) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', docType);
      fd.append('property_id', propertyId);
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/upload-document`, fd, { headers });
      setPropertyId(''); setDocType('');
      await onDone();
    } catch (e) { setErr(e?.response?.data?.error || 'Could not upload that.'); }
    finally { setBusy(false); }
  };
  if (!rentable.length) return null;
  return (
    <div data-testid="uw-rental-staff-lease-upload" style={{ display: 'grid', gap: 6, fontSize: 12 }}>
      <b style={{ fontSize: 13 }}>Add a lease or rent proof for the borrower</b>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 700 }}>Property (required)
          <select data-testid="uw-rental-staff-lease-property" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} style={{ ...input, display: 'block' }}>
            <option value="">Choose the property…</option>
            {rentable.map((p) => <option key={p.id} value={String(p.id)}>{`${p.kind === 'current_home' ? 'Current home' : 'Rental'} · ${p.address || `#${p.id}`}`}</option>)}
          </select></label>
        <label style={{ fontWeight: 700 }}>Document
          <select data-testid="uw-rental-staff-lease-type" value={docType} onChange={(e) => setDocType(e.target.value)} style={{ ...input, display: 'block' }}>
            <option value="">Choose…</option>
            {STAFF_PROPERTY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></label>
      </div>
      <input type="file" data-testid="uw-rental-staff-lease-file" disabled={!ready} onChange={(e) => upload(e.target.files && e.target.files[0])} style={{ fontSize: 12 }} />
      {!propertyId && <span data-testid="uw-rental-staff-lease-hint">Choose the property first.</span>}
      {busy && <span>Uploading…</span>}
      {err && <span role="alert">{err}</span>}
    </div>
  );
}

export function RentalPropertiesStaff({ API, borrowerId, headers, onChanged }) {
  const [st, setSt] = useState(null);
  const [ending, setEnding] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try { setSt((await axios.get(`${API}/api/admin/borrowers/${borrowerId}/rentals`, { headers })).data); } catch (_) { setSt(null); }
  }, [API, borrowerId, headers]);
  useEffect(() => { load(); }, [load]);
  if (!st || !(st.properties || []).length) return null;
  const end = async () => {
    setErr(null);
    try { await axios.post(`${API}/api/admin/borrowers/${borrowerId}/current-home/sale-ended`, { reason }, { headers }); setEnding(false); setReason(''); await load(); if (onChanged) await onChanged(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not save that.'); }
  };
  const cell = { padding: '10px 8px', verticalAlign: 'top' };
  const selling = st.plan && st.plan.plan === 'selling';
  return (
    <div data-testid="uw-rentals" style={{ ...card(), fontFamily: T.font, color: T.ink }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Rentals and the current home</h2>
      {st.plan && <div style={{ fontSize: 13 }}>Application: {st.plan.hasMortgage === false ? 'owns the current home free and clear' : st.plan.holder === 'private' ? 'private mortgage' : 'bank mortgage'} · {st.plan.plan || '—'}{st.plan.plan === 'keeping' ? (st.plan.rentOut ? ', renting it out' : ', not renting it out') : ''}</div>}
      {/* 🔑 THE ANSWER VS THE REPORT (backend utils/housingConflict, 2026-10-27): creditor and open month only. While one is
          open, mortgage history is not verified and no VOM is sent. STAFF ONLY. */}
      {(st.conflicts || []).filter((c) => c.status !== 'superseded').map((c) => (
        <div key={c.id} data-testid="uw-housing-conflict" data-kind={c.kind} data-status={c.status} role="status"
          style={{ background: c.status === 'open' ? T.tan : T.slateLight, borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.5 }}>
          <b>{c.kind === 'private_but_reported' ? 'Answered a private lender' : 'Answered no mortgage'}, but the credit report shows a mortgage with {c.creditor || 'a lender'}{c.opened ? ` (opened ${c.opened})` : ''}.</b>
          <div>{c.status === 'open' ? 'Open — mortgage history is not verified and no VOM is sent until the borrower answers.'
            : c.status === 'needs_staff' ? `"Something else"${c.answerText ? ` — \u201c${c.answerText}\u201d` : ''}. Still holding: resolve it with one of the other answers.`
            : `Answered${c.choice ? `: ${{ current_home: 'it is the current home\u2019s mortgage', other_property: 'a loan on a different property', something_else: 'something else' }[c.choice] || c.choice}` : ''}${c.answerText ? ` — \u201c${c.answerText}\u201d` : ''}.`}</div>
          {/* V2 (11-01): the button only for those who can use it (the route is allow-listed: requireAllowList('housing-conflict')). */}
          {['open', 'needs_staff'].includes(c.status) && st.canConfirmVom === true && <ResolveConflict API={API} borrowerId={borrowerId} headers={headers} conflict={c} onDone={async () => { await load(); if (onChanged) await onChanged(); }} />}
        </div>
      ))}
      {(st.vom || []).map((v) => <VomStaffCard key={v.id} API={API} borrowerId={borrowerId} headers={headers} v={v} canConfirm={st.canConfirmVom === true} onDone={async () => { await load(); if (onChanged) await onChanged(); }} />)}
      <DocumentConfirms API={API} borrowerId={borrowerId} headers={headers} module="housing" onChanged={async () => { await load(); if (onChanged) await onChanged(); }} />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 820 }}>
          <thead><tr style={{ textAlign: 'left', borderBottom: `2px solid ${T.ink}` }}>
            {['Property', 'How it counts', 'Rent', 'PITIA', 'Net / debt', 'Mortgage tradeline', 'Documents'].map((h) => <th key={h} scope="col" style={{ padding: '8px 8px' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {st.properties.map((p) => { const f = p.figures || {}; return (
              <tr key={p.id} data-testid="uw-rental-row" data-reason={f.reason || ''} style={{ borderBottom: `1px solid ${T.line}`, background: f.mismatch ? T.tan : 'transparent' }}>
                <td style={cell}><b>{p.kind === 'current_home' ? 'Current home' : p.kind === 'owned_other' ? 'Other property' : 'Rental'}</b><div>{p.address}</div></td>
                <td style={cell}>{RP_REASON[f.reason] || '—'}{f.proof && f.proof.by ? <div style={{ fontSize: 12 }}>proof: {f.proof.by === 'schedule_e' ? 'Schedule E' : `${f.proof.found} rent deposits`}</div> : null}
                  {f.paymentUnresolved ? <div data-testid="uw-rental-unresolved" style={{ fontSize: 12, fontWeight: 700 }}>{f.unresolvedReason} — the decision waits for it</div> : null}
                  {f.privateLender && !f.escrow && (f.missing || []).includes('tax_bill_unescrowed') ? <StaffPropertyUpload API={API} borrowerId={borrowerId} headers={headers} propertyId={p.id} role="tax_bill" label="tax statement" onDone={async () => { await load(); if (onChanged) await onChanged(); }} /> : null}
                  {f.privateLender && !f.escrow && (f.missing || []).includes('insurance_unescrowed') ? <StaffPropertyUpload API={API} borrowerId={borrowerId} headers={headers} propertyId={p.id} role="insurance" label="insurance declarations page" onDone={async () => { await load(); if (onChanged) await onChanged(); }} /> : null}
                  {f.privateLender ? <div style={{ fontSize: 12 }}>private lender: payment from the VOM{f.vom ? ` (${money(f.vom.monthlyPayment)}${f.vom.escrowed ? ', escrowed' : ', + tax and insurance'})` : ' — not in yet'}</div> : null}</td>
                <td style={cell} data-testid="uw-rental-rent" data-source={f.rentSource || ''}>{f.rent != null ? money(f.rent) : '—'}{f.rentSource === 'schedule_e' && f.scheduleE ? <div style={{ fontSize: 12 }}>Schedule E{f.scheduleE.taxYear ? ` ${f.scheduleE.taxYear}` : ''}: {money(f.scheduleE.annual)} a year ÷ 12 (no lease)</div> : f.rentSource === 'lease' ? <div style={{ fontSize: 12 }}>from the lease</div> : null}</td>
                <td style={cell}>{f.pitia != null ? money(f.pitia) : '—'}{f.escrow === false ? <div style={{ fontSize: 12 }}>no escrow — tax + insurance added</div> : null}{(f.missing || []).length ? <div style={{ fontSize: 12 }}>missing: {f.missing.join(', ')}</div> : null}</td>
                <td style={cell}>{f.reason === 'income' ? money(f.net) : f.debtAdd ? `debt ${money(f.debtAdd)}` : '—'}{f.debtExclude ? <div style={{ fontSize: 12 }}>tradeline {money(f.debtExclude)} replaced</div> : null}</td>
                <td style={cell}>{f.tradeline ? `${f.tradeline.creditor || 'mortgage'} · ${money(f.tradeline.payment)}${f.tradeline.matchedBy ? ` · matched ${{ account: 'by account number', creditor: 'by lender name', payment: 'by payment amount', elimination: 'by elimination', answered: 'by the conflict answer' }[f.tradeline.matchedBy] || f.tradeline.matchedBy}` : ''}` : p.free_and_clear ? 'free and clear' : f.privateLender ? 'none (private lender)' : 'not matched'}{f.mismatch ? <div style={{ fontSize: 12, fontWeight: 700 }}>statement {money(f.mismatch.statement)} vs report {money(f.mismatch.tradeline)} — the statement stands until you decide</div> : null}</td>
                <td style={cell}>{(p.docs || []).map((d) => <div key={d.id} style={{ fontSize: 12 }}>{d.role}: {d.name}{d.read ? '' : ' (reading)'}</div>)}</td>
              </tr>); })}
          </tbody>
        </table>
      </div>
      <StaffLeaseUpload API={API} borrowerId={borrowerId} headers={headers} properties={st.properties} onDone={async () => { await load(); if (onChanged) await onChanged(); }} />
      {selling && !ending && (
        <button type="button" data-testid="uw-sale-ended" style={{ ...btn(), justifySelf: 'start' }} onClick={() => setEnding(true)}>The sale ended (contract terminated or proceeds didn&rsquo;t arrive)</button>
      )}
      {ending && (
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 700 }}>What happened? (the payment then counts and the file is re-decided)
            <input data-testid="uw-sale-ended-reason" value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...input, width: '100%' }} /></label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" data-testid="uw-sale-ended-confirm" style={btn('primary', reason.trim().length < 10)} disabled={reason.trim().length < 10} onClick={end}>Count the payment and re-decide</button>
            <button type="button" style={btn()} onClick={() => setEnding(false)}>Cancel</button>
          </div>
        </div>
      )}
      {err && <div role="alert" style={{ fontSize: 13 }}>{err}</div>}
    </div>
  );
}

// 🔑 THE RENTER'S 12-MONTH TABLE ON HOUSING (Jesse, 2026-10-26 — backend utils/rentHistoryReview). Per month: the matched
// payment(s), the confidence, who showed it (the check, the borrower's pick, a cancelled check), days after the due day
// (30+ is late; after the 5th but under 30 is information only). A low-confidence, borrower-picked or check-shown month,
// and a step-6 dispute, wait here for Accept or Ask again — Housing can't be accepted while one is open. STAFF ONLY.
const RH_SOURCE = { check: 'the check', borrower: 'the borrower picked it', cheque: 'a cancelled check', staff_asked: 'asked again' };
export function RentHistoryTable({ API, borrowerId, headers, onChanged }) {
  const [st, setSt] = useState(null);
  const [ask, setAsk] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try { setSt((await axios.get(`${API}/api/admin/borrowers/${borrowerId}/rent-history`, { headers })).data); } catch (_) { setSt(null); }
  }, [API, borrowerId, headers]);
  useEffect(() => { load(); }, [load]);
  if (!st || !st.enabled || !(st.onFile > 0)) return null;
  const decide = async (month, decision, note = null) => {
    setErr(null);
    try { await axios.post(`${API}/api/admin/borrowers/${borrowerId}/rent-history/${month}`, { decision, note }, { headers }); setAsk(null); await load(); if (onChanged) await onChanged(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not save that.'); }
  };
  const cell = { padding: '10px 8px', verticalAlign: 'top' };
  return (
    <div data-testid="uw-rent-history" style={{ ...card(), fontFamily: T.font, color: T.ink }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Rent history from bank statements</h2>
        <div style={{ fontSize: 13 }}>{st.status === 'reading' ? 'Reading the statements…' : st.line}{st.expectedSource ? ` · expected from ${st.expectedSource === 'inferred' ? 'the statements' : st.expectedSource === 'lease' ? 'the lease' : 'the VOR'}` : ''}{st.dueDay ? ` · due day ${st.dueDay}` : ''}</div>
      </div>
      {st.dispute && st.dispute.open && (
        <div data-testid="uw-rent-dispute" role="status" style={{ background: T.tan, borderRadius: 8, padding: '10px 12px', fontSize: 13, display: 'grid', gap: 8 }}>
          <div><b>The borrower says this is not right:</b> &ldquo;{st.dispute.notes}&rdquo;</div>
          <div style={{ display: 'flex', gap: 8 }}><button type="button" data-testid="uw-rent-dispute-close" style={btn('primary')} onClick={() => decide('dispute', 'accepted')}>Looked at it — close</button></div>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 760 }}>
          <thead><tr style={{ textAlign: 'left', borderBottom: `2px solid ${T.ink}` }}>
            {['Month', 'Payment', 'Amount', 'Confidence', 'Shown by', 'Paid', ''].map((h) => <th key={h} scope="col" style={{ padding: '8px 8px' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(st.months || []).map((m) => (
              <tr key={m.month} data-testid="uw-rent-month" data-month={m.month} data-to-staff={m.toStaff ? 'true' : 'false'} style={{ borderBottom: `1px solid ${T.line}`, background: m.toStaff ? T.tan : m.resolved ? 'transparent' : T.slateLight }}>
                <td style={cell}><b>{m.month}</b></td>
                <td style={cell}>{(m.txns || []).length ? m.txns.map((t) => <div key={t.id || t.date}>{t.date} · {t.description}</div>) : m.verdict === 'no_statement' ? 'No statement' : m.source === 'cheque' ? 'Cancelled check uploaded' : m.verdict}</td>
                <td style={cell}>{m.amount != null ? money(m.amount) : (m.txns || []).length ? money(m.txns.reduce((a, t) => a + Number(t.amount || 0), 0)) : '—'}</td>
                <td style={cell}>{m.confidence || '—'}</td>
                <td style={cell}>{RH_SOURCE[m.source] || m.source || '—'}{m.staffAccepted ? ' · accepted' : ''}</td>
                <td style={cell}>{m.daysAfterDue == null ? '—' : m.late ? <b>{`${m.daysAfterDue} days after due — late`}</b> : m.afterFifth ? `after the 5th (${m.daysAfterDue} days after due)` : 'on time'}</td>
                <td style={cell}>
                  {m.toStaff && ask !== m.month && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" data-testid="uw-rent-month-accept" style={btn('primary')} onClick={() => decide(m.month, 'accepted')}>Accept</button>
                      <button type="button" data-testid="uw-rent-month-ask" style={btn()} onClick={() => setAsk(m.month)}>Ask again</button>
                    </div>
                  )}
                  {ask === m.month && <AskAgain onSend={(note) => decide(m.month, 'rejected', note)} onCancel={() => setAsk(null)} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {err && <div role="alert" style={{ fontSize: 13 }}>{err}</div>}
    </div>
  );
}
function AskAgain({ onSend, onCancel }) {
  const [note, setNote] = useState('');
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 700 }}>What should the borrower send? (the borrower sees this)
        <input data-testid="uw-rent-month-ask-note" value={note} onChange={(e) => setNote(e.target.value)} style={{ ...input, width: '100%' }} /></label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" data-testid="uw-rent-month-ask-send" style={btn('primary', note.trim().length < 10)} disabled={note.trim().length < 10} onClick={() => onSend(note.trim())}>Ask the borrower</button>
        <button type="button" style={btn()} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// 🔑 THE IDENTITY RECORD ON THE IDENTITY STEP (2026-10-26). The Docs step's mirror card held the only staff row that
// read the identity record ("Photos uploaded, not submitted", "Verified — Persona", the abandoned photos); with Docs
// read-only it lives here, from the same helper (utils/identityAdminLabel), never from documents on file.
const IDENTITY_PILL = { verified: null, photos_uploaded: 'Photos uploaded, not submitted', checking: 'Checking', needs_fix: 'Needs fixing',
  flagged: 'Flagged', on_hold: 'On hold', not_started: 'Not started' };
const IDENTITY_METHOD = { persona: 'Persona', manual_automatic: 'uploaded ID, automatic check', manual_staff: 'uploaded ID, staff review' };
export function IdentityRecordRow({ user, idVerification, documents }) {
  const s = identityStaffState(user, idVerification, documents);
  const pill = s.state === 'verified' ? `Verified — ${IDENTITY_METHOD[s.method] || 'identity record'}` : IDENTITY_PILL[s.state] || 'Not started';
  return (
    <div data-testid="uw-identity-record" data-state={s.state} style={{ ...card(), fontFamily: T.font, color: T.ink }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'baseline' }}>
        <span style={{ fontSize: 15, fontWeight: 800 }}>Government-Issued Photo ID</span>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{pill}</span>
      </div>
      {s.state === 'verified' && s.manualPhotos === 'uploaded_not_submitted' && (
        <div data-testid="admin-identity-photos-unsubmitted" style={{ fontSize: 13 }}>Manual ID photos: uploaded, not submitted — they complete nothing.</div>
      )}
    </div>
  );
}

// 🔑 "ALL DOCUMENTS", IN ITS OWN OVERLAY (LO plan v7 item 9, Jesse 2026-10-28 — ReviewModule draws the overlay). A partner's
// document reads "Partner" in From with the icon, whose staff tooltip and aria-label name the sender ("Sent by <name>,
// <firm>", GET /api/admin/borrowers/:id/partner-provenance — staff only). "What the partner saw ›" opens inside the
// same overlay when the submission has a frozen snapshot; a file without one has no link.
// 🔑 "UPLOAD A DOCUMENT" FOR STAFF (Jesse, 2026-10-29 — backend utils/staffModuleUpload): choose the type and, if there
// is one, the request it answers. With a request the document is the borrower's; without one it is STAFF ONLY until it is
// attached to a request (listed below the form with "Attach"). Every upload and attach is audit-logged on the server.
export function StaffUploadPanel({ API, borrowerId, headers, onUploaded }) {
  const [opts, setOpts] = useState(null);
  const [type, setType] = useState('');
  const [needId, setNeedId] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [attachTo, setAttachTo] = useState({});
  const [inputKey, setInputKey] = useState(0);
  const load = useCallback(() => {
    if (!API || !borrowerId) return;
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/module-upload`, { headers })
      .then((r) => setOpts(r.data || null)).catch(() => setOpts({ types: [], needs: [], staffOnly: [], error: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, borrowerId]);
  useEffect(() => { load(); }, [load]);
  if (!opts) return null;
  const needs = opts.needs || [];
  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('document_type', type);
      if (needId) fd.append('need_id', needId);
      const r = await axios.post(`${API}/api/admin/borrowers/${borrowerId}/module-upload`, fd, { headers });
      setMsg(r.data.visibility === 'admin_only' ? 'Uploaded. Staff only until you attach it to a request.' : 'Uploaded and attached to the request.');
      setType(''); setNeedId(''); setFile(null); setInputKey((k) => k + 1);
      load(); if (onUploaded) onUploaded();
    } catch (e) { setMsg(e?.response?.data?.error || 'The upload failed. Nothing was saved.'); }
    setBusy(false);
  };
  const attach = async (d) => {
    setBusy(true); setMsg(null);
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/module-upload/${d.id}/attach`, { need_id: attachTo[d.id] }, { headers });
      setMsg('Attached. The borrower can see it now.');
      load(); if (onUploaded) onUploaded();
    } catch (e) { setMsg(e?.response?.data?.error || 'Could not attach it.'); }
    setBusy(false);
  };
  const sel = { fontSize: 13.5, minHeight: 36, fontFamily: T.font, maxWidth: '100%' };
  return (
    <div data-testid="uw-staff-upload" style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: '12px 14px', display: 'grid', gap: 8 }}>
      <div style={{ fontWeight: 800, fontSize: 14.5 }}>Upload a document</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select data-testid="uw-staff-upload-type" aria-label="Document type" value={type} onChange={(e) => setType(e.target.value)} style={sel}>
          <option value="">Document type…</option>
          {(opts.types || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select data-testid="uw-staff-upload-need" aria-label="Request it answers" value={needId} onChange={(e) => setNeedId(e.target.value)} style={sel}>
          <option value="">No request (staff only)</option>
          {needs.map((n) => <option key={n.id} value={n.id}>{n.title}{n.borrowerName ? ` · ${n.borrowerName}` : ''}</option>)}
        </select>
        <input key={inputKey} type="file" data-testid="uw-staff-upload-file" aria-label="File" onChange={(e) => setFile(e.target.files && e.target.files[0])} style={{ fontSize: 13 }} />
        <button type="button" data-testid="uw-staff-upload-submit" disabled={busy || !type || !file} onClick={submit} style={btn('primary', busy || !type || !file)}>Upload</button>
      </div>
      {!needId && <div style={{ fontSize: 12.5 }}>Without a request, the document is staff only: the borrower does not see it until you attach it to a request.</div>}
      {msg && <div data-testid="uw-staff-upload-msg" role="status" style={{ fontSize: 13 }}>{msg}</div>}
      {(opts.staffOnly || []).length > 0 && (
        <div data-testid="uw-staff-only-docs" style={{ display: 'grid', gap: 6, borderTop: `1px solid ${T.line}`, paddingTop: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5 }}>Staff only · {opts.staffOnly.length}</div>
          {opts.staffOnly.map((d) => {
            const mine = needs.filter((n) => Number(n.userId) === Number(d.userId));
            return (
              <div key={d.id} data-testid="uw-staff-only-doc" data-doc-id={d.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 13.5 }}>
                <span><b>{d.name}</b> · {d.typeLabel}{d.uploadedAt ? ` · ${day(d.uploadedAt)}` : ''}</span>
                {mine.length > 0 ? (<>
                  <select data-testid="uw-staff-only-attach-need" aria-label="Attach to request" value={attachTo[d.id] || ''} onChange={(e) => setAttachTo({ ...attachTo, [d.id]: e.target.value })} style={sel}>
                    <option value="">Attach to a request…</option>
                    {mine.map((n) => <option key={n.id} value={n.id}>{n.title}</option>)}
                  </select>
                  <button type="button" data-testid="uw-staff-only-attach" disabled={busy || !attachTo[d.id]} onClick={() => attach(d)} style={btn('secondary', busy || !attachTo[d.id])}>Attach</button>
                </>) : <span style={{ fontSize: 12.5 }}>No open request to attach it to.</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DocumentsReadOnly({ documents, API, borrowerId, headers, onUploaded }) {
  const rows = (documents || []).filter((d) => !/__(replaced|unplaced|removed)$/.test(String(d.document_type || '')))
    .slice().sort((a, b) => new Date(b.upload_date || 0) - new Date(a.upload_date || 0));
  const [prov, setProv] = useState(null);
  const [view, setView] = useState('list');   // list | partner
  useEffect(() => {
    if (!API || !borrowerId) return undefined;
    let live = true;
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/partner-provenance`, { headers })
      .then((r) => { if (live) setProv(r.data || null); }).catch(() => { if (live) setProv(null); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, borrowerId]);
  const partnerIds = new Set(((prov && prov.documentIds) || []).map(Number));
  if (view === 'partner' && prov && prov.hasSnapshot) {
    return <PartnerSawView API={API} headers={headers} submissionId={prov.submissionId} onBack={() => setView('list')} />;
  }
  return (
    <div data-testid="uw-docs-readonly" style={{ ...card(), fontFamily: T.font, color: T.ink }}>
      {prov && prov.found && prov.hasSnapshot && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" data-testid="uw-partner-saw-link" onClick={() => setView('partner')}
            style={{ background: 'none', border: 'none', padding: 0, font: `700 14px ${T.font}`, color: T.navy, textDecoration: 'underline', cursor: 'pointer' }}>
            What the partner saw ›
          </button>
        </div>
      )}
      {/* 🔑 THE PARTNER'S LOAN AMOUNT / PURPOSE VS THE FILE (Jesse, 2026-10-28/29 — moved here from the retired Section
          Review row; backend utils/partnerProvenanceStaff.submissionMismatches). Staff only, never shown to the borrower. */}
      {prov && prov.found && (prov.mismatches || []).length > 0 && (
        <div data-testid="uw-partner-mismatches" style={{ border: `1px solid ${T.line}`, borderLeft: `5px solid ${T.tan}`, borderRadius: 10, padding: '10px 14px', fontSize: 13.5 }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>Partner submission vs the file</div>
          {prov.mismatches.map((m) => (
            <div key={m.field} data-testid="uw-partner-mismatch" data-field={m.field} style={{ marginBottom: 4 }}>
              <b>{m.field === 'loan.purpose' ? 'Loan purpose' : 'Loan amount'}</b>: partner {String(m.partner)} · file {String(m.file)}
              <div>{m.note}</div>
            </div>
          ))}
        </div>
      )}
      <StaffUploadPanel API={API} borrowerId={borrowerId} headers={headers} onUploaded={onUploaded} />
      {!rows.length && <div style={{ fontSize: 14 }}>No documents yet.</div>}
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 620 }}>
            <thead><tr style={{ textAlign: 'left', borderBottom: `2px solid ${T.ink}` }}>
              <th scope="col" style={{ padding: '8px 6px' }}>Document</th><th scope="col" style={{ padding: '8px 6px' }}>Type</th>
              <th scope="col" style={{ padding: '8px 6px' }}>Uploaded</th><th scope="col" style={{ padding: '8px 6px' }}>From</th>
            </tr></thead>
            <tbody>
              {rows.map((d) => {
                const fromPartner = partnerIds.has(Number(d.id));
                return (
                  <tr key={d.id} data-testid="uw-docs-row" data-partner={fromPartner ? 'true' : 'false'} style={{ borderBottom: `1px solid ${T.line}` }}>
                    <td style={{ padding: '8px 6px' }}>{d.original_name || d.filename || `#${d.id}`}</td>
                    <td style={{ padding: '8px 6px' }}>{String(d.document_type || '').replace(/^asset_.*/, 'Account statement').replace(/_/g, ' ')}</td>
                    <td style={{ padding: '8px 6px' }}>{d.upload_date ? day(d.upload_date) : '—'}</td>
                    <td data-testid="uw-docs-from" style={{ padding: '8px 6px' }}>
                      {fromPartner ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <SentIcon label={prov.sentBy || 'Sent by the partner'} testId="uw-docs-partner-icon" /> Partner
                        </span>
                      ) : d.source === 'borrower_upload' ? 'Borrower' : d.source === 'manual_id' ? 'Borrower (ID)' : d.source ? String(d.source).replace(/_/g, ' ') : 'Doorly'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// 🔑 "WHAT THE PARTNER SAW" (LO plan v7 item 10): the LO's Calculated income & assets step exactly as frozen at submit
// ("Fix this" as text — CalculatedLines frozen), "Open at submit:" by name, then the history — a plain-English line
// and the time each, event names and ids behind "Details", collapsed after 5 ("Show all N events"). Read-only; from
// GET /api/doorlypro/admin/submissions/:id/partner-saw (staff only).
export const PARTNER_SAW_COLLAPSED = 5;
export function PartnerSawView({ API, headers, submissionId, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState({});
  useEffect(() => {
    let live = true;
    axios.get(`${API}/api/doorlypro/admin/submissions/${submissionId}/partner-saw`, { headers })
      .then((r) => { if (live) setData(r.data); }).catch((e) => { if (live) setErr(e?.response?.data?.error || 'Could not load what the partner saw.'); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, submissionId]);
  const history = (data && data.history) || [];
  const shown = all ? history : history.slice(0, PARTNER_SAW_COLLAPSED);
  const who = data && Array.isArray(data.shown_to) && data.shown_to.length
    ? (data.shown_to.length > 1 ? `${data.shown_to[0]} (${data.shown_to[1]})` : data.shown_to[0]) : 'the partner';
  const calc = data && data.calculated;
  return (
    <div data-testid="uw-partner-saw" style={{ ...card(), fontFamily: T.font, color: T.ink }}>
      <button type="button" data-testid="uw-partner-saw-back" onClick={onBack} style={{ ...btn(), justifySelf: 'start', alignSelf: 'flex-start' }}>← All documents</button>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>What the partner saw before submitting</h2>
      {err && <div style={{ fontSize: 14 }}>{err}</div>}
      {!data && !err && <div style={{ fontSize: 14 }}>Loading…</div>}
      {data && (<>
        <div data-testid="uw-partner-saw-frozen" style={{ fontSize: 13.5 }}>
          Frozen at submit, {dayTime(data.frozen_at)}. Shown to {who} on the {(calc && calc.title) || 'Calculated income & assets'} step, exactly as below.
        </div>
        {calc && (
          <div data-testid="uw-partner-saw-calculated" style={{ border: `1px solid ${T.line}`, borderRadius: 12, padding: '14px 16px', background: T.white }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{calc.title}</div>
            {calc.intro && <p style={{ fontSize: 13.5, margin: '0 0 10px', lineHeight: 1.55 }}>{calc.intro}</p>}
            <CalculatedLines calc={{ lines: [], ...calc }} goTo={() => {}} frozen />
          </div>
        )}
        <div data-testid="uw-partner-saw-open" style={{ fontSize: 14 }}>
          <b>Open at submit:</b>
          {(data.open_at_submit || []).length
            ? <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>{data.open_at_submit.map((t, i) => <li key={i} data-testid="uw-partner-saw-open-item">{t}</li>)}</ul>
            : <span> nothing</span>}
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>History</div>
          {shown.map((e, i) => (
            <div key={i} data-testid="uw-partner-saw-event" style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, auto) 1fr auto', gap: 10, padding: '6px 0', borderTop: `1px solid ${T.line}`, fontSize: 13.5, alignItems: 'baseline' }}>
              <span style={{ whiteSpace: 'nowrap' }}>{dayTime(e.at)}</span>
              <span>{e.line}</span>
              <button type="button" data-testid="uw-partner-saw-details" aria-expanded={!!open[i]} onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
                style={{ background: 'none', border: 'none', padding: 0, font: `600 13px ${T.font}`, color: T.navy, textDecoration: 'underline', cursor: 'pointer' }}>
                {open[i] ? 'Hide details' : 'Details'}
              </button>
              {open[i] && <span data-testid="uw-partner-saw-meta" style={{ gridColumn: '2 / 4', fontSize: 12.5, overflowWrap: 'anywhere' }}>{e.details}</span>}
            </div>
          ))}
          {!all && history.length > PARTNER_SAW_COLLAPSED && (
            <button type="button" data-testid="uw-partner-saw-show-all" onClick={() => setAll(true)}
              style={{ background: 'none', border: 'none', padding: '8px 0 0', font: `700 13.5px ${T.font}`, color: T.ink, textDecoration: 'underline', cursor: 'pointer' }}>
              Show all {history.length} events
            </button>
          )}
        </div>
      </>)}
    </div>
  );
}
